import { FormEvent, lazy, ReactNode, Suspense, useEffect, useMemo, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import {
  ArrowRight, BookOpen, Bookmark, ChevronLeft, ChevronRight, CircleUserRound,
  Disc3, Download, Feather, FileQuestion, Heart, Library, LockKeyhole, LogIn, LogOut,
  Menu, MessageCircle, Music2, Pause, PenLine, Play, Plus, Search, Send,
  ShieldCheck, Shuffle, SkipBack, SkipForward, Sparkles, Star, Upload,
  UserRound, Volume2, X
} from 'lucide-react'
import { adminApi, apiUrl, authApi, compressAvatar, contentApi, feedbackApi, musicApi, session, submitApi, upload } from './api'
import { prepareArticle, ReaderMode, stableCoverPalette } from './reader'
import { extractPdfText } from './pdf'
type View = 'home' | 'library' | 'tags' | 'work' | 'articles' | 'books' | 'collections' | 'forum' | 'submit' | 'feedback' | 'profile' | 'admin'
type ThemeName = 'obsidian' | 'nebula' | 'sakura'
type WorkType = 'article' | 'column' | 'collection' | 'download'
type User = {
  id: string
  username: string
  display_name?: string
  role: 'owner' | 'admin' | 'user'
  avatar_url?: string
}
type Work = {
  id: string
  slug: string
  type: WorkType
  title: string
  author_text: string
  summary?: string
  cover_url?: string
  views?: number
  downloads?: number
  rating?: number
}
type Block = { id: string; type: 'text' | 'image' | 'quote'; value: string }
type Chapter = { id: string; title: string; blocks: Block[] }

const PdfViewer = lazy(() => import('./PdfViewer'))
const ForumPage = lazy(() => import('./ForumPage'))
const uid = () => crypto.randomUUID?.() || Math.random().toString(36).slice(2)
const routeAliases: Record<string,View> = { article:'articles', book:'books', collection:'collections', tag:'tags', archive:'library', contribute:'submit', account:'profile', discussion:'forum' }
const currentView = (): View => {
  const value = location.hash.replace('#/', '').split(/[/?]/)[0]
  const normalized = routeAliases[value] || value
  return (['home', 'library', 'tags', 'work', 'articles', 'books', 'collections', 'forum', 'submit', 'feedback', 'profile', 'admin'].includes(normalized) ? normalized : 'home') as View
}
const go = (view: View) => {
  location.hash = `#/${view}`
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

const workKinds: Array<{ key: WorkType; title: string; subtitle: string; icon: ReactNode }> = [
  { key: 'article', title: '普通文章', subtitle: '论述、随笔与史料札记', icon: <Feather /> },
  { key: 'column', title: '问答专栏', subtitle: '以问题组织深入回答', icon: <FileQuestion /> },
  { key: 'collection', title: '长篇合集', subtitle: '目录、章节与连续阅读', icon: <Library /> },
  { key: 'download', title: '下载典藏', subtitle: 'PDF、EPUB 与珍稀资料', icon: <Download /> }
]
const quotes = [
  ['未经审视的人生不值得过。', '苏格拉底'],
  ['历史从来不是过去的，它活在每一个当下。', '克罗齐'],
  ['人是一根会思想的芦苇。', '帕斯卡尔'],
  ['世界上只有一种真正的英雄主义，就是认清生活后依然热爱生活。', '罗曼·罗兰']
]

function Button({ children, tone = 'gold', type = 'button', onClick, disabled = false }: {
  children: ReactNode
  tone?: 'gold' | 'paper' | 'quiet'
  type?: 'button' | 'submit'
  onClick?: () => void
  disabled?: boolean
}) {
  return <button type={type} className={`button button-${tone}`} onClick={onClick} disabled={disabled}>{children}</button>
}

function Brand() {
  const [glow, setGlow] = useState(false)
  const activate = () => {
    setGlow(true)
    go('home')
    window.setTimeout(() => setGlow(false), 900)
  }
  return <button className={`brand ${glow ? 'brand-glow' : ''}`} onClick={activate} aria-label="争鸣书阁首页">
    <svg viewBox="0 0 72 72" aria-hidden="true">
      <circle cx="36" cy="36" r="27" />
      <ellipse cx="36" cy="36" rx="27" ry="10" />
      <ellipse cx="36" cy="36" rx="10" ry="27" transform="rotate(34 36 36)" />
      <path d="M20 48c10-5 21-5 32 0M21 50v6c10-4 20-4 30 0v-6" />
    </svg>
    <span><b>争鸣书阁</b><small>ZHENGMING ARCHIVE</small></span>
  </button>
}

function Stars() {
  const stars = useMemo(() => Array.from({ length: 22 }, (_, index) => ({
    left: (index * 37) % 100,
    top: (index * 61) % 100,
    size: index % 3 + 1,
    delay: (index % 8) * 0.4
  })), [])
  return <div className="star-field" aria-hidden="true">
    {stars.map((star, index) => <i key={index} style={{
      left: `${star.left}%`, top: `${star.top}%`, width: star.size,
      height: star.size, animationDelay: `${star.delay}s`
    }} />)}
  </div>
}

function ThemeSwitch({ theme, setTheme }: {
  theme: ThemeName
  setTheme: (theme: ThemeName) => void
}) {
  const themes: Array<{ key: ThemeName; label: string }> = [
    { key: 'sakura', label: '月白樱粉' },
    { key: 'nebula', label: '星云蓝紫' },
    { key: 'obsidian', label: '曜石明夜' }
  ]
  return <div className="theme-switch" aria-label="切换配色">
    {themes.map(item => <button
      key={item.key}
      className={`${item.key} ${theme === item.key ? 'active' : ''}`}
      onClick={() => setTheme(item.key)}
      title={item.label}
      aria-label={item.label}
    ><i /></button>)}
  </div>
}

function Header({ user, openAuth, theme, setTheme }: {
  user: User | null
  openAuth: () => void
  theme: ThemeName
  setTheme: (theme: ThemeName) => void
}) {
  const [menu, setMenu] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)
  const [searchText, setSearchText] = useState('')
  const openSearch = () => setSearchOpen(value => !value)
  const submitSearch = (event: FormEvent) => {
    event.preventDefault()
    const query = searchText.trim()
    if (!query) return
    location.hash = `#/library?query=${encodeURIComponent(query)}`
    setSearchOpen(false)
    setMenu(false)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }
  const links: Array<[View, string]> = [
    ['home', '首页'], ['library', '书库'], ['tags', '标签'], ['forum', '论坛'], ['submit', '投稿'],
    ['feedback', '反馈'], ['profile', '我的书房']
  ]
  return <header className="site-header">
    <Brand />
    <nav className={menu ? 'nav-open' : ''}>
      {links.map(([view, label]) => <button key={view} onClick={() => { go(view); setMenu(false) }}>{label}</button>)}
      <a className="friend-site" href="https://derengle.pages.dev" target="_blank" rel="noreferrer" title="访问友站 derengle.pages.dev">友站<small>DERENGLE</small></a>
      {(user?.role === 'owner' || user?.role === 'admin') &&
        <button onClick={() => { go('admin'); setMenu(false) }}>馆务后台</button>}
    </nav>
    <div className="header-actions">
      <ThemeSwitch theme={theme} setTheme={setTheme} />
      <form className={`header-search ${searchOpen ? 'open' : ''}`} onSubmit={submitSearch}>
        <input value={searchText} onChange={event => setSearchText(event.target.value)} placeholder="搜索标题、标签或正文" aria-label="搜索站内文章" />
        <button type="submit" aria-label="提交搜索"><Search /></button>
      </form>
      <button className="round-button" onClick={openSearch} aria-label="展开站内搜索" title="搜索站内文章"><Search /></button>
      <button className="account-button" onClick={user ? () => go('profile') : openAuth}>
        {user ? <><UserRound />{user.display_name || user.username}</> : <><LogIn />登录</>}
      </button>
      <button className="mobile-menu" onClick={() => setMenu(!menu)}>{menu ? <X /> : <Menu />}</button>
    </div>
  </header>
}

function PageHero({ eyebrow, title, text, variant = '' }: { eyebrow: string; title: string; text: string; variant?: string }) {
  return <section className={`page-hero ${variant}`}>
    <Stars />
    <div className="page-nebula nebula-one" aria-hidden="true" />
    <div className="page-nebula nebula-two" aria-hidden="true" />
    <div className="constellation constellation-one" aria-hidden="true"><i /><i /><i /><i /><span /></div>
    <div className="constellation constellation-two" aria-hidden="true"><i /><i /><i /><span /></div>
    <span className="meteor meteor-one" aria-hidden="true" />
    <span className="meteor meteor-two" aria-hidden="true" />
    <span className="eyebrow">{eyebrow}</span>
    <h1>{title}</h1>
    <p>{text}</p>
    <div className="hero-arc" />
  </section>
}

function EmptyState({ title = '尚无馆藏', text = '这里保持为空，等待正式投稿与后台发布。' }: {
  title?: string
  text?: string
}) {
  return <div className="empty-state">
    <div className="empty-orbit"><BookOpen /></div>
    <h3>{title}</h3>
    <p>{text}</p>
    <Button onClick={() => go('submit')}><PenLine />成为首位投稿者</Button>
  </div>
}

function DefaultCover({ work, large = false }: { work: Work; large?: boolean }) {
  const [background, accent, foreground] = stableCoverPalette(work.id || work.slug || work.title)
  const title = Array.from(work.title || '未命名作品')
  const lines: string[] = []
  const lineLength = large ? 8 : 7
  for (let index = 0; index < title.length && lines.length < 4; index += lineLength) lines.push(title.slice(index, index + lineLength).join(''))
  return <svg className="default-cover-svg" viewBox="0 0 360 520" role="img" aria-label={`${work.title} 默认封面`}>
    <defs><linearGradient id={`cover-${work.id}`} x1="0" y1="0" x2="1" y2="1"><stop stopColor={background}/><stop offset="1" stopColor={accent}/></linearGradient></defs>
    <rect width="360" height="520" rx="8" fill={`url(#cover-${work.id})`}/><rect x="22" y="22" width="316" height="476" rx="3" fill="none" stroke={foreground} opacity=".38"/>
    <path d="M45 100h270M45 420h270" stroke={foreground} opacity=".22"/>
    <text x="180" y="152" textAnchor="middle" fill={foreground} fontSize={large ? 31 : 28} fontFamily="serif" fontWeight="600">
      {lines.map((line, index) => <tspan x="180" dy={index ? 48 : 0} key={line + index}>{line}</tspan>)}
    </text>
    <text x="180" y="390" textAnchor="middle" fill={foreground} opacity=".82" fontSize="15" fontFamily="serif">{work.author_text || '佚名'}</text>
    <g transform="translate(145 438)"><rect width="70" height="42" rx="3" fill={foreground} opacity=".9"/><text x="35" y="27" textAnchor="middle" fill={background} fontSize="13" fontFamily="serif">争鸣书阁</text></g>
  </svg>
}

function WorkGrid({ works }: { works: Work[] }) {
  return <div className="work-grid">
    {works.map((work, index) => <motion.article
      key={work.id}
      initial={{ opacity: 0, y: 22 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.04 }}
      className="work-card"
    >
      <div className={`work-cover cover-${work.type}`}
        style={work.cover_url ? { backgroundImage: `url(${work.cover_url})` } : undefined}>
        {!work.cover_url && <DefaultCover work={work} />}
        <span>{workKinds.find(item => item.key === work.type)?.title}</span>
      </div>
      <div className="work-copy">
        <small>{work.author_text || '佚名'} · {work.views || 0} 次阅览</small>
        <h3>{work.title}</h3>
        <p>{work.summary || '该作品尚未填写概要。'}</p>
        <div className="work-bottom">
          <span><Star />{work.rating || '暂无评分'}</span>
          <button onClick={() => { location.hash = `#/${work.type === 'collection' ? 'collections' : work.type === 'download' ? 'books' : 'articles'}/${work.slug}` }}>作品主页<ArrowRight /></button>
        </div>
      </div>
    </motion.article>)}
  </div>
}

function OpeningScene() {
  const [visible, setVisible] = useState(() => sessionStorage.getItem('zm_opening_seen') !== '1')

  useEffect(() => {
    if (!visible) return
    const timer = window.setTimeout(() => {
      sessionStorage.setItem('zm_opening_seen', '1')
      setVisible(false)
    }, 2800)
    return () => window.clearTimeout(timer)
  }, [visible])

  const close = () => {
    sessionStorage.setItem('zm_opening_seen', '1')
    setVisible(false)
  }

  return <AnimatePresence>{visible && <motion.div
    className="opening-scene"
    initial={{ opacity: 1 }}
    exit={{ opacity: 0, scale: 1.04 }}
    transition={{ duration: .7 }}
  >
    <div className="opening-stars" />
    <div className="opening-rings"><i /><i /><i /><span><Brand /></span></div>
    <motion.div className="opening-copy" initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: .45 }}>
      <small>ZHENGMING ARCHIVE · 思想入夜</small>
      <h1>争鸣书阁</h1>
      <p>让思想在此相遇，让时间得以被阅读。</p>
    </motion.div>
    <div className="opening-progress"><i /></div>
    <button onClick={close}>跳过开场</button>
  </motion.div>}</AnimatePresence>
}

function ParticleWorlds() {
  const worlds = [
    { key: 'mars', name: '火星', latin: 'MARS', note: '争辩与行动的赤色颗粒' },
    { key: 'moon', name: '月球', latin: 'LUNA', note: '记忆与沉思的月白碎片' },
    { key: 'sun', name: '太阳', latin: 'SOL', note: '知识与启蒙的恒星光核' }
  ]
  return <section className="particle-worlds section">
    <div className="section-heading"><div><span className="section-kicker">THREE CELESTIAL IDEAS · 三重天体</span><h2>思想也有自己的引力与形态</h2></div></div>
    <div className="world-grid">{worlds.map((world, worldIndex) => <article className={`particle-world world-${world.key}`} key={world.key}>
      <div className="particle-sphere" aria-hidden="true">
        {Array.from({ length: 54 }, (_, index) => <i key={index} style={{
          '--i': index,
          '--x': ((index * 37 + worldIndex * 11) % 100) - 50,
          '--y': ((index * 61 + worldIndex * 17) % 100) - 50,
          '--s': index % 5 + 2,
          '--d': (index % 12) * -.18
        } as any} />)}
        <span className="world-core" />
      </div>
      <small>{world.latin}</small><h3>{world.name}</h3><p>{world.note}</p>
    </article>)}</div>
  </section>
}

function BookPortal() {
  const [active, setActive] = useState(false)
  return <button className={`book-portal ${active ? 'active' : ''}`} onClick={() => setActive(!active)} aria-label="开启思想之书">
    <svg className="book-svg" viewBox="0 0 560 440" aria-hidden="true">
      <defs>
        <linearGradient id="pagePink" x1="0" y1="0" x2="1" y2="1"><stop stopColor="#fffafb"/><stop offset="1" stopColor="#f4c7d8"/></linearGradient>
        <linearGradient id="inkPink" x1="0" y1="0" x2="1" y2="0"><stop stopColor="#b94f7d"/><stop offset="1" stopColor="#df8ead"/></linearGradient>
        <filter id="bookGlow"><feGaussianBlur stdDeviation="8" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
      </defs>
      <path className="book-shadow" d="M78 340 Q280 390 482 340"/>
      <path className="book-page left" d="M280 337C232 304 165 294 86 315V116c78-22 146-10 194 28z"/>
      <path className="book-page right" d="M280 337c48-33 115-43 194-22V116c-78-22-146-10-194 28z"/>
      <path className="book-spine" d="M280 144v193"/>
      <path className="page-line l1" d="M126 168c52-8 92-2 126 13"/><path className="page-line l2" d="M126 209c52-8 92-2 126 13"/><path className="page-line l3" d="M126 250c52-8 92-2 126 13"/>
      <path className="page-line r1" d="M308 181c34-15 74-21 126-13"/><path className="page-line r2" d="M308 222c34-15 74-21 126-13"/><path className="page-line r3" d="M308 263c34-15 74-21 126-13"/>
      <g className="book-streams" filter="url(#bookGlow)"><path d="M278 139C206 84 168 84 112 54"/><path d="M282 139c72-55 110-55 166-85"/><path d="M280 139C270 77 280 52 280 17"/></g>
      <g className="book-sparks"><circle cx="111" cy="54" r="5"/><circle cx="448" cy="54" r="5"/><circle cx="280" cy="17" r="5"/><circle cx="177" cy="86" r="3"/><circle cx="383" cy="84" r="3"/></g>
    </svg>
    <span><b>{active ? '思想正在流动' : '开启思想之书'}</b><small>{active ? '再次点击，令书页静息' : '点击后，流线缓缓升起'}</small></span>
  </button>
}

function FlowBook() {
  const [active, setActive] = useState(false)
  return <button className={`flow-book ${active ? 'active' : ''}`} onClick={() => setActive(!active)} aria-label="展开思想之书">
    <svg viewBox="0 0 560 420" aria-hidden="true">
      <defs>
        <linearGradient id="bookGold" x1="0" y1="0" x2="1" y2="1"><stop stopColor="currentColor"/><stop offset="1" stopColor="transparent"/></linearGradient>
      </defs>
      <path className="book-page page-left" d="M276 306C218 270 148 266 82 294V118c72-29 139-20 194 24z"/>
      <path className="book-page page-right" d="M284 306c58-36 128-40 194-12V118c-72-29-139-20-194 24z"/>
      <path className="book-spine" d="M280 139v169"/>
      <path className="book-line line-one" d="M111 159c54-16 102-10 137 8"/>
      <path className="book-line line-two" d="M111 199c52-14 99-8 137 9"/>
      <path className="book-line line-three" d="M449 159c-54-16-102-10-137 8"/>
      <path className="book-line line-four" d="M449 199c-52-14-99-8-137 9"/>
      <path className="flow flow-a" d="M280 142C241 92 180 76 115 91"/>
      <path className="flow flow-b" d="M280 142c39-50 100-66 165-51"/>
      <path className="flow flow-c" d="M280 139C272 67 293 38 342 21"/>
      <circle className="book-star star-a" cx="114" cy="91" r="4"/>
      <circle className="book-star star-b" cx="445" cy="91" r="4"/>
      <circle className="book-star star-c" cx="342" cy="21" r="4"/>
    </svg>
    <span><b>{active ? '思想正在流动' : '点击展开书页'}</b><small>轻触，让文字与光线缓缓苏醒</small></span>
  </button>
}

function TagsPage() {
  const [tags, setTags] = useState<Array<{name:string;slug:string;count:number}>>([])
  const [active, setActive] = useState<string>()
  const [items, setItems] = useState<Work[]>([])
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(false)
  const [query, setQuery] = useState('')
  const [sort, setSort] = useState<'hot'|'name'>('hot')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    contentApi.tagCloud(1, 120).then((result:any) => setTags(result.tags || [])).catch(() => setTags([])).finally(() => setLoading(false))
  }, [])

  const openTag = async (slug: string, nextPage = 1) => {
    if (active === slug && nextPage === 1) { setActive(undefined); setItems([]); return }
    setActive(slug)
    const result: any = await contentApi.tagWorks(slug, nextPage, 8)
    setItems(nextPage === 1 ? (result.works || []) : [...items, ...(result.works || [])])
    setPage(nextPage)
    setHasMore(Boolean(result.hasMore))
  }

  const visible = tags.filter(tag => tag.name.toLowerCase().includes(query.trim().toLowerCase()))
    .sort((left, right) => sort === 'hot' ? Number(right.count) - Number(left.count) : left.name.localeCompare(right.name, 'zh-CN'))
  const max = Math.max(1, ...tags.map(tag => Number(tag.count) || 1))

  return <main className="tags-page">
    <PageHero eyebrow="TAG INDEX · 本站标签" title="循着主题，进入思想的内部" text="标签与文章数量实时对应；点击可展开文章列表，下方以轻量标签云浏览全站主题。" />
    <section className="section tag-index">
      <div className="tag-controls"><label className="search-field"><Search/><input value={query} onChange={event => setQuery(event.target.value)} placeholder="搜索标签"/></label><div className="filter-tabs"><button className={sort === 'hot' ? 'active' : ''} onClick={() => setSort('hot')}>按热度</button><button className={sort === 'name' ? 'active' : ''} onClick={() => setSort('name')}>按名称</button></div></div>
      {loading ? <div className="loading"><span className="loading-orbit"/>正在整理标签</div> : <div className="tag-accordion">{visible.map(tag => <article className={active === tag.slug ? 'active' : ''} key={tag.slug}>
        <button className="tag-summary" onClick={() => openTag(tag.slug)}><span>{tag.name}</span><b>{tag.count}</b><ChevronRight/></button>
        {active === tag.slug && <div className="tag-results">{items.length ? <><WorkGrid works={items}/>{hasMore && <Button tone="paper" onClick={() => openTag(tag.slug, page + 1)}>加载更多</Button>}</> : <div className="panel-empty">该标签暂时没有公开文章。</div>}</div>}
      </article>)}</div>}
    </section>
    <section className="section lightweight-cloud"><div className="section-heading"><div><span className="section-kicker">LIGHTWEIGHT CLOUD</span><h2>标签云</h2></div></div><div className="tag-chips">{visible.map(tag => {
      const level = Math.max(0, Math.min(4, Math.round((Number(tag.count) / max) * 4)))
      return <button className={`level-${level}`} key={tag.slug} onClick={() => openTag(tag.slug)}>{tag.name}<small>{tag.count}</small></button>
    })}</div></section>
  </main>
}

function Home({ works }: { works: Work[] }) {
  const [quote, setQuote] = useState(0)
  return <main>
    <section className="home-hero">
      <Stars />
      <div className="hero-copy">
        <span className="eyebrow">等待历史天使群 · 公共人文典藏</span>
        <motion.h1 initial={{ opacity: 0, y: 25 }} animate={{ opacity: 1, y: 0 }}>
          让思想在此<em>争鸣</em><br />让时间得以被阅读
        </motion.h1>
        <p>一座由共同体持续建设的数字书阁。收藏历史、哲学、文学与社会思想，也收藏每一次严肃的追问、辨析与回应。</p>
        <div className="hero-buttons">
          <Button onClick={() => go('library')}>进入书库<ArrowRight /></Button>
          <Button tone="paper" onClick={() => go('submit')}><PenLine />投递作品</Button>
        </div>
      </div>
      <div className="cosmos book-cosmos"><BookPortal /></div>
    </section>

    <section className="section manifesto">
      <span className="section-kicker">OUR PURPOSE · 立阁之旨</span>
      <div>
        <h2>不是信息的仓库，<br />而是<em>思想的会面之所</em>。</h2>
        <div className="prose">
          <p>争鸣书阁为“等待历史天使”群体服务。我们不追逐碎片化热点，而愿意把值得反复阅读的文字保存下来。</p>
          <p>这里既接纳经典，也重视群友自己的研究、翻译、札记与提问。每一篇作品都应经得起阅读，每一次评论都应尊重思想。</p>
        </div>
      </div>
    </section>

    <section className="section home-tag-entry reveal-section"><div><span className="section-kicker">TAG INDEX · 标签索引</span><h2>按主题浏览本站文章</h2><p>标签云已经移到独立页面，首页因此更轻、更快。</p></div><Button tone="paper" onClick={() => go('tags')}>进入标签页<ArrowRight/></Button></section>

    <section className="section quote-stage reveal-section">
      <div className="quote-aurora aurora-one" aria-hidden="true" />
      <div className="quote-aurora aurora-two" aria-hidden="true" />
      <div className="quote-stardust" aria-hidden="true"><i /><i /><i /><i /><i /><i /></div>
      <div className="quote-orbit" aria-hidden="true" />
      <span>{String(quote + 1).padStart(2, '0')} / {String(quotes.length).padStart(2, '0')}</span>
      <AnimatePresence mode="wait">
        <motion.blockquote key={quote} initial={{ opacity: 0, rotateX: 16 }} animate={{ opacity: 1, rotateX: 0 }} exit={{ opacity: 0 }}>
          “{quotes[quote][0]}”<cite>— {quotes[quote][1]}</cite>
        </motion.blockquote>
      </AnimatePresence>
      <div className="quote-controls">
        <button onClick={() => setQuote((quote - 1 + quotes.length) % quotes.length)}><ChevronLeft /></button>
        <div>{quotes.map((_, index) => <i className={index === quote ? 'active' : ''} key={index} />)}</div>
        <button onClick={() => setQuote((quote + 1) % quotes.length)}><ChevronRight /></button>
      </div>
    </section>

    <section className="section">
      <div className="section-heading">
        <div><span className="section-kicker">COLLECTIONS · 馆藏门类</span><h2>从不同路径，抵达人的世界</h2></div>
        <button onClick={() => go('library')}>浏览全部<ArrowRight /></button>
      </div>
      <div className="kind-grid">
        {workKinds.map((item, index) => <motion.button key={item.key} whileHover={{ y: -8 }} onClick={() => go('library')}>
          <small>0{index + 1}</small><span>{item.icon}</span><h3>{item.title}</h3><p>{item.subtitle}</p><ArrowRight />
        </motion.button>)}
      </div>
    </section>

    <section className="section arrivals">
      <div className="section-heading">
        <div><span className="section-kicker">NEW ARRIVALS · 新近入阁</span><h2>最近被保存的思想</h2></div>
      </div>
      {works.length ? <WorkGrid works={works.slice(0, 6)} /> : <EmptyState />}
    </section>

    <section className="section stats">
      {[['馆藏作品', works.length], ['累计阅读', works.reduce((sum, item) => sum + (item.views || 0), 0)], ['思想门类', 4], ['虚构示例', 0]]
        .map(([label, value]) => <div key={label}><strong>{value}</strong><span>{label}</span></div>)}
    </section>

    <section className="rights-notice">
      <svg viewBox="0 0 120 18" aria-hidden="true">
        <path d="M1 9h42M77 9h42M49 9c6-8 16-8 22 0-6 8-16 8-22 0Z"/>
        <circle cx="60" cy="9" r="2.4"/>
      </svg>
      <p>如果侵犯了您的资产阶级法权，请联系 <a href="mailto:yangty104@gmail.com">yangty104@gmail.com</a></p>
    </section>
  </main>
}

function LibraryPage({ works, loading }: { works: Work[]; loading: boolean }) {
  const initialQuery = useMemo(() => {
    const raw = location.hash.split('?')[1] || ''
    return new URLSearchParams(raw).get('query') || ''
  }, [location.hash])
  const [query, setQuery] = useState(initialQuery)
  const [type, setType] = useState<'all' | WorkType>('all')
  const [remote, setRemote] = useState<any[]>([])
  const [searching, setSearching] = useState(false)
  const [exact, setExact] = useState(true)

  useEffect(() => {
    const value = query.trim()
    if (!value) { setRemote([]); setExact(true); return }
    const timer = window.setTimeout(async () => {
      setSearching(true)
      try {
        const result: any = await contentApi.search(value)
        setRemote(result.results || [])
        setExact(result.exact !== false)
      } catch { setRemote([]); setExact(true) }
      finally { setSearching(false) }
    }, 260)
    return () => window.clearTimeout(timer)
  }, [query])

  const source = query.trim() ? remote : works
  const result = source.filter(work => type === 'all' || work.type === type)
  return <main>
    <PageHero eyebrow="THE ARCHIVE · 公共书库" title="在人文世界中，寻找你的坐标" text="可搜索标题、作者、标签、简介和正文；无精确结果时自动推荐相似内容。" />
    <section className="section library-section">
      <label className="search-field"><Search /><input data-library-search value={query} onChange={event => setQuery(event.target.value)} placeholder="搜索标题、作者、标签或正文……" /></label>
      {query && !searching && result.length > 0 && <div className={`search-mode ${exact ? 'exact' : 'similar'}`}><Sparkles />{exact ? `找到与“${query}”相关的内容` : `没有完全匹配，以下为与“${query}”相似的内容`}</div>}
      <div className="filter-tabs">
        <button className={type === 'all' ? 'active' : ''} onClick={() => setType('all')}>全部</button>
        {workKinds.map(item => <button className={type === item.key ? 'active' : ''} onClick={() => setType(item.key)} key={item.key}>{item.title}</button>)}
      </div>
      {loading || searching ? <div className="loading"><span className="loading-orbit" />正在检索馆藏</div> :
        result.length ? <WorkGrid works={result} /> :
          <EmptyState title={query ? '没有找到相近作品' : '馆藏仍待开启'}
            text={query ? '可以缩短关键词，或改用人物、主题和文章中的具体文字。' : '不放置虚构文章；所有公开作品都来自正式投稿或馆务发布。'} />}
    </section>
  </main>
}

function RichEditor({ blocks, setBlocks }: { blocks: Block[]; setBlocks: (blocks: Block[]) => void }) {
  const update = (id: string, value: string) => setBlocks(blocks.map(block => block.id === id ? { ...block, value } : block))
  const add = (type: Block['type']) => setBlocks([...blocks, { id: uid(), type, value: '' }])
  const uploadImage = async (id: string, file?: File) => {
    if (!file) return
    const result: any = await upload(file, 'content')
    update(id, result.url || result.object_url || result.key || '')
  }
  return <div className="rich-editor">
    {blocks.map((block, index) => <div className={`editor-block block-${block.type}`} key={block.id}>
      <span className="block-number">{String(index + 1).padStart(2, '0')}</span>
      {block.type === 'image'
        ? <label className="image-insert">
          {block.value ? <img src={block.value} alt="" /> : <><Upload /><b>在当前位置插入图片</b><small>JPG / PNG / GIF / WebP</small></>}
          <input type="file" accept="image/*" onChange={event => uploadImage(block.id, event.target.files?.[0])} />
        </label>
        : <textarea rows={block.type === 'quote' ? 3 : 8} value={block.value}
          onChange={event => update(block.id, event.target.value)}
          placeholder={block.type === 'quote' ? '输入引文与出处……' : '输入正文。可随时在段落之间插入图片……'} />}
      <button className="delete-block" onClick={() => setBlocks(blocks.filter(item => item.id !== block.id))}><X /></button>
    </div>)}
    <div className="editor-tools">
      <button onClick={() => add('text')}><Plus />文字段落</button>
      <button onClick={() => add('image')}><Plus />正文图片</button>
      <button onClick={() => add('quote')}><Plus />引文</button>
    </div>
  </div>
}

function SubmitPage({ user, openAuth }: { user: User | null; openAuth: () => void }) {
  const [type, setType] = useState<WorkType>('article')
  const [title, setTitle] = useState('')
  const [author, setAuthor] = useState(user?.display_name || user?.username || '')
  const [question, setQuestion] = useState('')
  const [summary, setSummary] = useState('')
  const [tags, setTags] = useState('')
  const [cover, setCover] = useState<File | null>()
  const [downloadFile, setDownloadFile] = useState<File | null>()
  const [parsedFileText, setParsedFileText] = useState('')
  const [parseProgress, setParseProgress] = useState('')
  const [blocks, setBlocks] = useState<Block[]>([{ id: uid(), type: 'text', value: '' }])
  const [chapters, setChapters] = useState<Chapter[]>([
    { id: uid(), title: '第一章', blocks: [{ id: uid(), type: 'text', value: '' }] }
  ])
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState('')

  const bodyText = type === 'collection'
    ? chapters.flatMap(chapter => chapter.blocks).map(block => block.value).join('\n')
    : blocks.map(block => block.value).join('\n')

  const chooseDownloadFile = async (file?: File) => {
    setDownloadFile(file || null); setParsedFileText(''); setParseProgress('')
    if (!file) return
    if (!title.trim()) setTitle(file.name.replace(/\.[^.]+$/, ''))
    if (file.type !== 'application/pdf' && !/\.pdf$/i.test(file.name)) return
    setParseProgress('正在加载 PDF 解析器……')
    try {
      const result = await extractPdfText(file, (page,total) => setParseProgress(`正在解析 PDF：${page} / ${total} 页`))
      setParsedFileText(result.text)
      setParseProgress(result.text.replace(/\s+/g,'').length ? `已解析 ${result.pageCount} 页，可生成简介、标签或转换为文章正文。` : `已读取 ${result.pageCount} 页，但没有检测到文本层；仍可在线阅读。`)
    } catch (error:any) { setParseProgress(error.message || 'PDF 解析失败，文件仍可正常上传。') }
  }
  const convertPdfToArticle = () => {
    if (!parsedFileText.trim()) return setMessage('该 PDF 没有可转换的文本。')
    setType('article')
    setBlocks([{ id:uid(), type:'text', value:parsedFileText }])
    setMessage('PDF 文本已转换为文章正文草稿，请检查页眉、页脚和断行。')
  }

  const generateMetadata = async () => {
    const fileName = downloadFile?.name || cover?.name || ''
    if (!bodyText.trim() && !title.trim() && !fileName) return setMessage('请至少填写作品名或选择一个文件。')
    setBusy(true)
    try {
      const result: any = await submitApi.aiMetadata({
        text: (parsedFileText || bodyText).trim(), title: title.trim(), author: author.trim(), fileName, type
      })
      setSummary(result.summary || '')
      setTags((result.tags || []).join('、'))
      setMessage('AI 已生成元数据，你仍可人工修改。')
    } catch (error: any) {
      setMessage(error.message || 'AI 服务尚未配置。')
    } finally { setBusy(false) }
  }

  const submit = async () => {
    if (!user) return openAuth()
    if (!title.trim() || !author.trim()) return setMessage('作品名称与作者不能为空。')
    if (type !== 'download' && !bodyText.trim()) return setMessage('非下载类作品必须在站内填写正文。')
    if (type === 'column' && !question.trim()) return setMessage('专栏必须先提出一个明确问题。')
    if (type === 'collection' && chapters.some(chapter => !chapter.title.trim() || !chapter.blocks.some(block => block.value.trim()))) {
      return setMessage('合集中的每个章节都必须填写标题与正文。')
    }
    if (type === 'download' && !downloadFile) return setMessage('下载类作品必须选择文件。')
    setBusy(true)
    try {
      let coverAttachmentId: string | undefined
      let sourceAttachmentId: string | undefined
      if (cover) {
        const result: any = await upload(cover, 'content')
        coverAttachmentId = result.attachmentId || result.id
      }
      if (downloadFile) {
        const result: any = await upload(downloadFile, 'download')
        sourceAttachmentId = result.attachmentId || result.id
        if ((downloadFile.type === 'application/pdf' || /\.pdf$/i.test(downloadFile.name)) && sourceAttachmentId) {
          const pageCount = Number(parseProgress.match(/(?:已解析|已读取)\s+(\d+)\s+页/)?.[1] || 0)
          await submitApi.pdfMetadata(sourceAttachmentId,pageCount,parsedFileText).catch(() => undefined)
        }
      }
      await submitApi.create({
        proposedType: type,
        title,
        authorText: author,
        metadata: {
          summary,
          tags: tags.split(/[、,，]/).filter(Boolean),
          question,
          coverAttachmentId
        },
        content: type === 'collection' ? { chapters } : { blocks },
        sourceAttachmentId,
        status: 'submitted'
      })
      setMessage('投稿已进入审稿室；审核通过后才会公开。')
    } catch (error: any) {
      setMessage(error.message || '投稿失败。')
    } finally { setBusy(false) }
  }

  return <main>
    <PageHero eyebrow="CONTRIBUTE · 共建书阁" title="把值得留下的文字，郑重地交给时间" text="四种作品采用不同编辑逻辑；所有公开内容都须经过馆务审核。" />
    <section className="section submission-layout">
      <aside className="type-sidebar">
        <h3>选择作品形态</h3>
        {workKinds.map(item => <button className={type === item.key ? 'active' : ''} onClick={() => setType(item.key)} key={item.key}>
          <span>{item.icon}</span><div><b>{item.title}</b><small>{item.subtitle}</small></div><ChevronRight />
        </button>)}
        <div className="rule-card"><ShieldCheck /><p><b>内容规则</b>文章、专栏和合集必须站内编辑正文；只有下载类接受文档导入。图片可插入正文任意位置。</p></div>
      </aside>

      <div className="submission-editor">
        <div className="editor-title">
          <div><small>当前创作模式</small><h2>{workKinds.find(item => item.key === type)?.title}</h2></div>
          <span>提交后进入馆务审核</span>
        </div>

        {!user && <div className="login-banner">
          <LockKeyhole /><div><b>登录后才能正式提交</b><p>可以先体验编辑，提交时再登录。</p></div>
          <Button onClick={openAuth}>登录 / 注册</Button>
        </div>}

        <div className="form-grid">
          <label><span>作品名称 *</span><input value={title} onChange={event => setTitle(event.target.value)} placeholder="输入完整作品名" /></label>
          <label><span>作者 / 责任者 *</span><input value={author} onChange={event => setAuthor(event.target.value)} placeholder="作者、译者或整理者" /></label>
          <label className="full-field"><span>作品封面（可选）</span>
            <div className="file-control"><Upload /><div><b>{cover?.name || '选择封面图片'}</b><small>没有封面时，以标题生成艺术文字封面</small></div>
              <input type="file" accept="image/*" onChange={event => setCover(event.target.files?.[0] || null)} />
            </div>
          </label>
        </div>

        {type === 'column' && <label className="question-control">
          <span><FileQuestion />专栏核心问题 *</span>
          <textarea rows={3} value={question} onChange={event => setQuestion(event.target.value)}
            placeholder="例如：历史叙事是否可能保持价值中立？" />
        </label>}

        {type === 'collection' ? <div className="chapter-editor">
          <div className="subheading"><div><small>TABLE OF CONTENTS</small><h3>目录与分章正文</h3></div>
            <Button tone="paper" onClick={() => setChapters([...chapters, {
              id: uid(), title: `第${chapters.length + 1}章`, blocks: [{ id: uid(), type: 'text', value: '' }]
            }])}><Plus />添加章节</Button>
          </div>
          {chapters.map((chapter, index) => <section className="chapter-card" key={chapter.id}>
            <div className="chapter-number">{String(index + 1).padStart(2, '0')}</div>
            <div className="chapter-content">
              <input value={chapter.title} onChange={event => setChapters(chapters.map(item =>
                item.id === chapter.id ? { ...item, title: event.target.value } : item
              ))} placeholder="章节标题" />
              <RichEditor blocks={chapter.blocks} setBlocks={next => setChapters(chapters.map(item =>
                item.id === chapter.id ? { ...item, blocks: next } : item
              ))} />
            </div>
            <button className="chapter-delete" onClick={() => setChapters(chapters.filter(item => item.id !== chapter.id))}><X /></button>
          </section>)}
        </div> : type === 'download' ? <label className="download-control">
          <Download /><h3>{downloadFile?.name || '上传典藏文件'}</h3>
          <p>适用于难以在线排版的 PDF、EPUB、TXT 或资料包。</p><span>点击选择文件</span>
          <input type="file" accept=".pdf,.epub,.txt,.zip" onChange={event => chooseDownloadFile(event.target.files?.[0])} />
          {parseProgress && <small className="pdf-parse-status">{parseProgress}</small>}
          {parsedFileText && <button type="button" className="button button-paper pdf-convert-button" onClick={event => { event.preventDefault(); convertPdfToArticle() }}>转换为站内文章草稿</button>}
        </label> : <div>
          <div className="subheading"><div><small>RICH TEXT COMPOSITION</small><h3>{type === 'column' ? '回答正文' : '文章正文'} *</h3></div>
            <span>文字、图片与引文可自由交错</span>
          </div>
          <RichEditor blocks={blocks} setBlocks={setBlocks} />
          <details className="syntax-help"><summary>排版语法帮助</summary><p><code>**加粗**</code>、<code>~~删除线~~</code>、<code>{'{{汉字|hàn zì}}'}</code> 注音、<code>正文[^1]</code> 与 <code>[^1]: 注释内容</code>。旧式 <code>[1]</code> 注释也会自动识别。</p></details>
        </div>}

        <div className="metadata-panel">
          <div className="subheading"><div><small>AI ASSISTED METADATA</small><h3>简介与主题标签</h3></div>
            <Button tone="paper" onClick={generateMetadata} disabled={busy}><Sparkles />AI 智能生成</Button>
          </div>
          <label><span>作品简介</span><textarea rows={5} value={summary} onChange={event => setSummary(event.target.value)} placeholder="可人工填写，也可由 AI 生成" /></label>
          <label><span>标签（以顿号分隔）</span><input value={tags} onChange={event => setTags(event.target.value)} placeholder="历史、思想史、现代性……" /></label>
        </div>

        {message && <div className="form-message">{message}</div>}
        <div className="submit-bar">
          <span><ShieldCheck />作品不会未经审核直接公开。</span>
          <Button onClick={submit} disabled={busy}>{busy ? '正在处理……' : <><Send />提交审核</>}</Button>
        </div>
      </div>
    </section>
  </main>
}

function WorkPage({ user, openAuth }: { user: User | null; openAuth: () => void }) {
  const routeParts = location.hash.replace(/^#\//,'').split('/')
  const slug = decodeURIComponent(routeParts.slice(1).join('/'))
  const [data, setData] = useState<any>()
  const [comments, setComments] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [reading, setReading] = useState(false)
  const [pdfReading, setPdfReading] = useState(false)
  const [chapter, setChapter] = useState(0)
  const [fontSize, setFontSize] = useState(() => Number(localStorage.getItem('zm_reader_font') || 18))
  const [readerMode, setReaderMode] = useState<ReaderMode>(() => localStorage.getItem('zm_reader_mode') === 'scroll' ? 'scroll' : 'paged')
  const [readerPage, setReaderPage] = useState(0)
  const [touchStart, setTouchStart] = useState<{x:number;y:number} | null>(null)
  const [tocOpen, setTocOpen] = useState(false)
  const [tocQuery, setTocQuery] = useState('')
  const [activeFootnote, setActiveFootnote] = useState<string>()
  const [commentText, setCommentText] = useState('')
  const [commentImage, setCommentImage] = useState<File | null>()
  const [replyTo, setReplyTo] = useState<string | undefined>()
  const [collapsedComments, setCollapsedComments] = useState<Set<string>>(() => new Set())
  const [message, setMessage] = useState('')

  const refreshComments = async (workId: string) => {
    const result: any = await contentApi.comments(workId)
    setComments(result.comments || [])
  }

  useEffect(() => {
    setLoading(true)
    contentApi.detail(slug).then(async (result: any) => {
      setData(result)
      const savedChapter = Number(localStorage.getItem(`zm_reader_chapter_${result.work.id}`) || 0)
      if (Number.isFinite(savedChapter) && savedChapter >= 0 && savedChapter < (result.work.chapters?.length || 1)) setChapter(savedChapter)
      await contentApi.view(result.work.id).catch(() => undefined)
      await refreshComments(result.work.id)
    }).catch((error: any) => setMessage(error.message)).finally(() => setLoading(false))
  }, [slug])

  if (loading) return <main><div className="loading work-loading"><Disc3/>正在展开作品</div></main>
  if (!data?.work) return <main><PageHero eyebrow="NOT FOUND" title="作品不存在或尚未公开" text="请返回书库继续浏览。"/></main>

  const work = data.work
  const currentChapter = work.chapters?.[chapter]
  const articleHtml = work.type === 'collection'
    ? currentChapter?.rendered_html || ''
    : work.type === 'column'
      ? work.questions?.[0]?.answer_html || work.rendered_html || ''
      : work.rendered_html || ''
  const preparedArticle = prepareArticle(articleHtml, 1200)
  const visibleChapters = (work.chapters || []).map((item:any, index:number) => ({ item, index }))
    .filter(({item}:any) => item.title.toLowerCase().includes(tocQuery.trim().toLowerCase()))
  const footnote = activeFootnote ? preparedArticle.footnotes[activeFootnote] : undefined
  const changeReaderMode = (mode: ReaderMode) => { setReaderMode(mode); localStorage.setItem('zm_reader_mode', mode); setReaderPage(0) }
  const changeReaderPage = (page:number) => {
    const next = Math.max(0, Math.min(preparedArticle.pages.length - 1, page))
    setReaderPage(next)
    localStorage.setItem(`zm_reader_page_${work.id}_${currentChapter?.id || 'main'}`, String(next))
    contentApi.progress(work.id, preparedArticle.pages.length > 1 ? next / (preparedArticle.pages.length - 1) : 1, currentChapter?.id, { mode:'paged', page:next }).catch(() => undefined)
  }
  const changeFontSize = (value: number) => { setFontSize(value); localStorage.setItem('zm_reader_font', String(value)) }
  const selectChapter = (index: number) => {
    setChapter(index); setReaderPage(0); setTocOpen(false)
    const item = work.chapters?.[index]
    localStorage.setItem(`zm_reader_chapter_${work.id}`, String(index))
    contentApi.progress(work.id, index / Math.max(1, work.chapters.length - 1), item?.id, { mode: readerMode, page: 0 }).catch(() => undefined)
  }

  const toggleFavorite = async () => {
    if (!user) return openAuth()
    const favorite = !data.viewer.favorite
    if (favorite) await contentApi.favorite(work.id)
    else await contentApi.unfavorite(work.id)
    setData({ ...data, viewer: { ...data.viewer, favorite } })
  }
  const rate = async (score: number) => {
    if (!user) return openAuth()
    await contentApi.rate(work.id, score)
    setData({ ...data, viewer: { ...data.viewer, rating: score } })
  }
  const postComment = async () => {
    if (!user) return openAuth()
    let imageId: string | undefined
    if (commentImage) {
      const result: any = await upload(commentImage, 'comment')
      imageId = result.attachmentId || result.id
    }
    await contentApi.comment(work.id, commentText, replyTo, imageId)
    setCommentText(''); setCommentImage(null); setReplyTo(undefined)
    await refreshComments(work.id)
  }
  const react = async (id: string, reaction: 'like' | 'dislike') => {
    if (!user) return openAuth()
    await contentApi.react(id, reaction)
    await refreshComments(work.id)
  }
  const removeComment = async (id: string) => {
    await contentApi.deleteComment(id)
    await refreshComments(work.id)
  }

  const replyTarget = comments.find(comment => comment.id === replyTo)
  const childrenByParent = new Map<string, any[]>()
  comments.forEach(comment => {
    if (!comment.parent_id) return
    const group = childrenByParent.get(comment.parent_id) || []
    group.push(comment)
    childrenByParent.set(comment.parent_id, group)
  })
  const toggleComment = (id:string) => setCollapsedComments(current => {
    const next = new Set(current)
    next.has(id) ? next.delete(id) : next.add(id)
    return next
  })
const renderComment = (comment:any): ReactNode => {
    const children = childrenByParent.get(comment.id) || []
    const collapsed = collapsedComments.has(comment.id)
    return <article className="comment-thread" key={comment.id} style={{ '--comment-depth': Math.min(Number(comment.depth || 0), 5) } as any}>
      <div className="comment"><div className="comment-avatar">{comment.avatar_url ? <img src={comment.avatar_url}/> : <UserRound/>}</div>
        <div><header><b>{comment.display_name || comment.username}</b><span>{comment.role === 'owner' ? '站长' : comment.role === 'admin' ? '管理员' : '读者'}</span><time>{new Date(comment.created_at).toLocaleString()}</time></header>
          {comment.reply_to_username && <small className="reply-to-user">回复 {comment.reply_to_display_name || comment.reply_to_username}</small>}
          <p>{comment.text}</p>{comment.image_url && <img className="comment-photo" src={comment.image_url}/>} {comment.edited_at && <small className="edited-mark">已编辑</small>}
          <footer><button onClick={() => react(comment.id,'like')}>赞 {comment.likes}</button><button onClick={() => react(comment.id,'dislike')}>踩 {comment.dislikes}</button><button onClick={() => setReplyTo(comment.id)}>回复</button>
            {children.length > 0 && <button onClick={() => toggleComment(comment.id)}>{collapsed ? `展开 ${children.length} 条回复` : '折叠回复'}</button>}
            {(user?.id === comment.user_id || user?.role === 'owner' || (user?.role === 'admin' && comment.role === 'user')) && <button onClick={() => removeComment(comment.id)}>删除</button>}</footer>
        </div></div>
      {!collapsed && children.length > 0 && <div className="comment-children">{children.map(renderComment)}</div>}
    </article>
  }

  if (pdfReading && work.download?.mime_type === 'application/pdf') return <Suspense fallback={<main><div className="loading work-loading"><span className="loading-orbit"/>正在加载 PDF 阅读器</div></main>}><PdfViewer url={apiUrl(work.download.url)} title={work.title} close={() => setPdfReading(false)}/></Suspense>

  if (reading) return <main className={`reader-page reader-${readerMode}`}>
    <div className="reader-toolbar">
      <button onClick={() => setReading(false)}><ChevronLeft/>返回作品主页</button>
      <strong>{work.title}{currentChapter ? ` · ${currentChapter.title}` : ''}</strong>
      <div className="reader-tools">
        {work.type === 'collection' && <button onClick={() => setTocOpen(true)}><Menu/><span>目录</span></button>}
        <button className={readerMode === 'paged' ? 'active' : ''} onClick={() => changeReaderMode('paged')}>分页</button><button className={readerMode === 'scroll' ? 'active' : ''} onClick={() => changeReaderMode('scroll')}>滚动</button>
        <button onClick={() => changeFontSize(Math.max(15, fontSize - 1))}>A−</button><span>{fontSize}</span><button onClick={() => changeFontSize(Math.min(28, fontSize + 1))}>A＋</button>
      </div>
    </div>
    {tocOpen && <><button className="reader-toc-backdrop" aria-label="关闭目录" onClick={() => setTocOpen(false)}/><aside className="reader-toc open"><header><h3>合集目录</h3><button onClick={() => setTocOpen(false)}><X/></button></header><label><Search/><input value={tocQuery} onChange={event => setTocQuery(event.target.value)} placeholder="搜索章节"/></label>{visibleChapters.map(({item,index}:any) =>
      <button className={index === chapter ? 'active' : ''} onClick={() => selectChapter(index)} key={item.id}><span>{String(index + 1).padStart(2, '0')}</span><b>{item.title}</b></button>)}</aside></>}
    <article className="reader-paper" style={{ fontSize }} tabIndex={0} onKeyDown={event => {
      if (event.key === 'ArrowRight' || event.key === 'PageDown' || event.key === 'ArrowDown') { event.preventDefault(); changeReaderPage(readerPage + 1) }
      if (event.key === 'ArrowLeft' || event.key === 'PageUp' || event.key === 'ArrowUp') { event.preventDefault(); changeReaderPage(readerPage - 1) }
      if (event.key === 'Escape') { activeFootnote ? setActiveFootnote(undefined) : tocOpen ? setTocOpen(false) : setReading(false) }
    }} onTouchStart={event => setTouchStart({ x:event.touches[0].clientX, y:event.touches[0].clientY })} onTouchEnd={event => {
      if (!touchStart || readerMode !== 'paged') return
      const dx = event.changedTouches[0].clientX - touchStart.x
      const dy = event.changedTouches[0].clientY - touchStart.y
      setTouchStart(null)
      if (Math.max(Math.abs(dx),Math.abs(dy)) < 45) return
      if (Math.abs(dx) > Math.abs(dy) ? dx < 0 : dy < 0) changeReaderPage(readerPage + 1)
      else changeReaderPage(readerPage - 1)
    }} onClick={event => {
      const target = event.target as HTMLElement
      const reference = target.closest<HTMLElement>('.reader-footnote-ref')
      if (reference?.dataset.footnote) { event.stopPropagation(); setActiveFootnote(reference.dataset.footnote); return }
      if (readerMode !== 'paged' || target.closest('a,button,input,textarea,ruby')) return
      const rect = event.currentTarget.getBoundingClientRect()
      changeReaderPage(event.clientX < rect.left + rect.width / 2 ? readerPage - 1 : readerPage + 1)
    }}>
      {work.type === 'column' && work.questions?.[0] && <div className="reader-question"><FileQuestion/><h1>{work.questions[0].question}</h1></div>}
      {currentChapter && <h1>{currentChapter.title}</h1>}
      <div className="reader-body" dangerouslySetInnerHTML={{ __html: readerMode === 'paged' ? preparedArticle.pages[readerPage] : preparedArticle.html }} />
      {readerMode === 'paged' && <div className="reader-page-status"><button disabled={readerPage === 0} onClick={event => { event.stopPropagation(); changeReaderPage(readerPage - 1) }}><ChevronLeft/>上一页</button><span>{readerPage + 1} / {preparedArticle.pages.length}</span><button disabled={readerPage >= preparedArticle.pages.length - 1} onClick={event => { event.stopPropagation(); changeReaderPage(readerPage + 1) }}>下一页<ChevronRight/></button></div>}
    </article>
    {footnote && <div className="footnote-layer" onClick={() => setActiveFootnote(undefined)}><aside className="footnote-card" onClick={event => event.stopPropagation()}><header><b>注释 [{footnote.id}]</b><button onClick={() => setActiveFootnote(undefined)}><X/></button></header><div dangerouslySetInnerHTML={{__html:footnote.html}}/></aside></div>}
    {work.type === 'collection' && <div className="chapter-navigation">
      <Button tone="paper" disabled={chapter === 0} onClick={() => selectChapter(chapter - 1)}><ChevronLeft/>上一章</Button>
      <Button disabled={chapter >= work.chapters.length - 1} onClick={() => selectChapter(chapter + 1)}>下一章<ChevronRight/></Button>
    </div>}
  </main>

  return <main className="work-page">
    <section className="work-hero"><Stars/>
      <div className={`work-main-cover cover-${work.type}`} style={work.cover_url ? { backgroundImage: `url(${work.cover_url})` } : undefined}>
        {!work.cover_url && <DefaultCover work={work} large />}
      </div>
      <div className="work-intro">
        <span className="eyebrow">{workKinds.find(item => item.key === work.type)?.title} · ZHENGMING ARCHIVE</span>
        <h1>{work.title}</h1><p className="work-author">{work.author_text}</p>
        <p className="work-summary">{work.summary || '该作品尚未填写概要。'}</p>
        <div className="work-tags">{work.tags?.map((tag: any) => <span key={tag.slug}>{tag.name}</span>)}</div>
        <div className="work-numbers"><span><b>{work.views || 0}</b>浏览</span><span><b>{work.downloads || 0}</b>下载</span><span><b>{work.rating || '—'}</b>评分</span></div>
        <div className="work-actions">
          {work.type !== 'download' && <Button onClick={() => {
            const saved = Number(localStorage.getItem(`zm_reader_page_${work.id}_${currentChapter?.id || 'main'}`) || 0)
            setReaderPage(Number.isFinite(saved) ? saved : 0)
            setReading(true)
          }}><BookOpen/>开始阅读</Button>}
          {work.download?.mime_type === 'application/pdf' && <Button onClick={() => setPdfReading(true)}><BookOpen/>在线阅读 PDF</Button>}
          {work.download && <a className="button button-gold" href={apiUrl(work.download.url)}><Download/>下载原文件</a>}
          <Button tone="paper" onClick={toggleFavorite}>{data.viewer.favorite ? <Heart fill="currentColor"/> : <Bookmark/>}{data.viewer.favorite ? '已收藏' : '收藏'}</Button>
        </div>
      </div>
    </section>

    <section className="section work-community">
      <div className="rating-panel"><div><span className="section-kicker">READER RATING</span><h2>你如何评价这部作品？</h2></div>
        <div className="star-rating">{[1,2,3,4,5].map(score => <button className={(data.viewer.rating || 0) >= score ? 'active' : ''} onClick={() => rate(score)} key={score}><Star fill="currentColor"/></button>)}</div>
      </div>
      {work.type !== 'download' && <div className="export-panel"><div><h3>离线典藏</h3><p>系统根据站内正文即时生成文件，并统计下载次数。</p></div>
        <a href={apiUrl(`/api/works/${work.id}/export.txt`)}><Download/>TXT</a><a href={apiUrl(`/api/works/${work.id}/export.epub`)}><Download/>EPUB</a>
      </div>}

      <section className="comments-panel">
        <div className="section-heading"><div><span className="section-kicker">DISCUSSION · 书后争鸣</span><h2>评论与回应</h2></div></div>
        <div className="comment-composer">
          {replyTo && <div className="reply-indicator">正在回复 {replyTarget?.display_name || replyTarget?.username || '一条评论'} <button onClick={() => setReplyTo(undefined)}>取消</button></div>}
          <textarea value={commentText} onChange={event => setCommentText(event.target.value)} placeholder={user ? '写下有依据、有分寸的回应……' : '登录后才能参与讨论'} disabled={!user}/>
          <div><label className="comment-image"><Upload/>{commentImage?.name || '插入图片'}<input type="file" accept="image/*" onChange={event => setCommentImage(event.target.files?.[0] || null)}/></label>
            <Button onClick={postComment}><Send/>发表评论</Button></div>
        </div>
        <div className="comment-list">{comments.length ? comments.filter(comment => !comment.parent_id).map(renderComment) : <EmptyState title="尚无评论" text="成为第一位认真回应这部作品的读者。"/>}</div>
      </section>
      {message && <div className="form-message">{message}</div>}
    </section>
  </main>
}

function FeedbackPage({ user, openAuth }: { user: User | null; openAuth: () => void }) {
  const categories = [
    ['bug', '网页修复', '布局错位、登录异常、动画或兼容问题'],
    ['feature', '功能完善', '希望增加或改进的平台能力'],
    ['article', '文章问题', '作品信息、排版、下载或内容错误'],
    ['content', '内容反馈', '评论秩序、版权、违规或敏感内容'],
    ['other', '其他建议', '任何值得被站长认真阅读的意见']
  ]
  const [category, setCategory] = useState('bug')
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [image, setImage] = useState<File | null>()
  const [items, setItems] = useState<any[]>([])
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState('')

  const refresh = () => {
    if (!user) return
    feedbackApi.mine().then((result: any) => setItems(result.feedback || []))
      .catch((error: any) => setMessage(error.message || '反馈记录加载失败'))
  }
  useEffect(() => { refresh() }, [user?.id])

  const submitFeedback = async () => {
    if (!user) return openAuth()
    if (!title.trim() || !body.trim()) return setMessage('请填写反馈标题和详细说明。')
    setBusy(true)
    try {
      let imageId
      if (image) {
        const result: any = await upload(image, 'comment')
        imageId = result.attachmentId || result.id
      }
      await feedbackApi.create({
        category,
        title,
        body,
        pageUrl: location.href,
        imageId
      })
      setTitle(''); setBody(''); setImage(null)
      setMessage('反馈已送达站长工作台。感谢你帮助书阁变得更好。')
      await refresh()
    } catch (error: any) {
      setMessage(error.message || '反馈提交失败')
    } finally { setBusy(false) }
  }

  return <main className="feedback-page">
    <PageHero
      variant="feedback-cosmos"
      eyebrow="FEEDBACK ORBIT · 反馈中心"
      title="让每一个问题，都得到清楚的回应"
      text="网页修复、功能完善、文章问题与内容建议，会直接进入站长工作台。"
    />
    <section className="section feedback-layout">
      <aside className="feedback-guide">
        <span className="section-kicker">CHOOSE A SIGNAL</span>
        <h2>选择反馈类型</h2>
        {categories.map(([key, name, description]) => <button
          key={key}
          className={category === key ? 'active' : ''}
          onClick={() => setCategory(key)}
        >
          <i /><div><b>{name}</b><small>{description}</small></div><ChevronRight />
        </button>)}
        <div className="feedback-promise"><ShieldCheck /><p>只有站长能够查看全部反馈并回复；普通管理员无权管理反馈记录。</p></div>
      </aside>

      <div className="feedback-main">
        {!user && <div className="login-banner"><LockKeyhole /><div><b>登录后提交反馈</b><p>登录可以让你跟踪状态并收到站长回复。</p></div><Button onClick={openAuth}>登录 / 注册</Button></div>}
        <div className="feedback-form">
          <span className="section-kicker">SEND A SIGNAL</span>
          <h2>描述你遇到的问题或期待</h2>
          <label><span>反馈标题 *</span><input value={title} onChange={event => setTitle(event.target.value)} placeholder="用一句话概括问题"/></label>
          <label><span>详细说明 *</span><textarea rows={9} value={body} onChange={event => setBody(event.target.value)} placeholder="请说明出现位置、操作步骤、期待结果；文章问题可附作品名称。"/></label>
          <label className="feedback-upload"><Upload /><div><b>{image?.name || '添加截图（可选）'}</b><small>支持 JPG / PNG / GIF / WebP</small></div><input type="file" accept="image/*" onChange={event => setImage(event.target.files?.[0] || null)}/></label>
          <div className="feedback-page-url"><small>当前页面会自动附带</small><code>{location.href}</code></div>
          {message && <div className="form-message">{message}</div>}
          <Button onClick={submitFeedback} disabled={busy}>{busy ? '正在发送……' : <><Send />提交反馈</>}</Button>
        </div>

        {user && <section className="my-feedback">
          <div className="section-heading"><div><span className="section-kicker">MY SIGNALS</span><h2>我的反馈记录</h2></div></div>
          {items.length ? items.map(item => <article key={item.id}>
            <div className={`feedback-status status-${item.status}`}>{item.status === 'open' ? '待查看' : item.status === 'processing' ? '处理中' : item.status === 'resolved' ? '已解决' : '已驳回'}</div>
            <div><small>{categories.find(value => value[0] === item.category)?.[1]} · {new Date(item.created_at).toLocaleString()}</small><h3>{item.title}</h3><p>{item.body}</p>
              {item.image_url && <img src={item.image_url} alt="反馈截图"/>}
              {item.owner_reply && <blockquote><b>站长回复</b>{item.owner_reply}</blockquote>}
            </div>
          </article>) : <div className="panel-empty">还没有提交过反馈。</div>}
        </section>}
      </div>
    </section>
  </main>
}

function ProfilePage({ user, openAuth, logout }: { user: User | null; openAuth: () => void; logout: () => void }) {
  const [space, setSpace] = useState<any>()
  const [message, setMessage] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [username, setUsername] = useState('')
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const refresh = () => authApi.me().then((result:any) => {
    setSpace(result)
    setDisplayName(result.user?.display_name || result.user?.username || '')
    setUsername(result.user?.username || '')
  }).catch((error:any) => setMessage(error.message))

  useEffect(() => { if (user) refresh() }, [user?.id])

  if (!user) return <main className="guest-study-page">
    <PageHero
      variant="profile-cosmos"
      eyebrow="MEMBER SPACE · 个人书房"
      title="保存你的阅读轨迹"
      text="收藏、进度、投稿与互动，都归档在个人书房。"
    />
    <section className="guest-study">
      <div className="guest-study-orbit" aria-hidden="true">
        <i /><i /><i /><span><BookOpen /></span>
      </div>
      <span className="section-kicker">PRIVATE READING CONSTELLATION</span>
      <h2>尚未登录</h2>
      <p>注册后即可评论、评分、收藏作品，并在不同设备间同步阅读进度。</p>
      <Button onClick={openAuth}><LogIn />登录或创建账号</Button>
      <div className="guest-benefits">
        <span><Bookmark />收藏馆藏</span>
        <span><BookOpen />同步进度</span>
        <span><MessageCircle />参与争鸣</span>
      </div>
    </section>
  </main>

  const profile = space?.user || user
  const favorites = space?.favorites || []
  const progress = space?.progress || []
  const notifications = space?.notifications || []

  const changeAvatar = async (file?: File) => {
    if (!file) return
    try {
      setMessage('正在压缩并上传头像……')
      await upload(await compressAvatar(file), 'avatar')
      await refresh()
      setMessage('新头像已提交，等待站长后台审核。')
    } catch (error:any) { setMessage(error.message || '头像上传失败') }
  }
  const saveAccount = async () => {
    try {
      await authApi.updateProfile(displayName, username)
      await refresh()
      setMessage('账号资料已更新。用户名修改会在下次登录时生效。')
    } catch (error:any) { setMessage(error.message || '账号资料更新失败') }
  }
  const savePassword = async () => {
    if (newPassword.length < 6) return setMessage('新密码至少需要 6 位。')
    try {
      await authApi.changePassword(currentPassword, newPassword)
      setCurrentPassword(''); setNewPassword('')
      setMessage('密码已修改，请重新登录。')
      window.setTimeout(() => logout(), 900)
    } catch (error:any) { setMessage(error.message || '密码修改失败') }
  }
  const removeAccount = async () => {
    if (!confirm('注销后账号将无法恢复，收藏和登录状态会被清除。确定继续吗？')) return
    await authApi.remove()
    await logout()
  }

  return <main>
    <PageHero variant="profile-cosmos" eyebrow="MEMBER SPACE · 个人书房" title={`欢迎回来，${profile.display_name || profile.username}`} text="你的阅读并非一次性经过，而会在这里形成自己的思想路径。" />
    <section className="section profile-layout">
      <aside className="identity-card">
        <label className="large-avatar avatar-change">{profile.avatar_url ? <img src={profile.avatar_url} alt="" /> : <CircleUserRound />}
          <span>更换头像</span><input type="file" accept=".png,.jpg,.jpeg,.gif,.webp" onChange={event => changeAvatar(event.target.files?.[0])}/>
        </label>
        <h2>{profile.display_name || profile.username}</h2><p>@{profile.username}</p>
        <span className={`role-badge role-${profile.role}`}>{profile.role === 'owner' ? '站长' : profile.role === 'admin' ? '管理员' : '读者'}</span>
        {profile.avatar_status === 'pending' && <small className="avatar-pending">新头像正在等待审核</small>}
        <Button tone="paper" onClick={logout}><LogOut />退出登录</Button>
        {profile.role !== 'owner' && <button className="danger-link" onClick={removeAccount}>注销账号</button>}
      </aside>
      <div className="profile-main">
        <div className="profile-stats"><div><strong>{favorites.length}</strong><span>收藏作品</span></div><div><strong>{progress.length}</strong><span>阅读记录</span></div><div><strong>{notifications.length}</strong><span>互动消息</span></div></div>
        {message && <div className="form-message">{message}</div>}
        <section className="panel account-settings"><h3><UserRound />账号资料</h3>
          <div className="account-form">
            <label><span>显示名称</span><input value={displayName} onChange={event => setDisplayName(event.target.value)} placeholder="展示给其他读者的名称"/></label>
            <label><span>登录用户名</span><input value={username} onChange={event => setUsername(event.target.value)} placeholder="3—24 位字符"/></label>
            <Button onClick={saveAccount}>保存资料</Button>
          </div>
        </section>
        <section className="panel account-settings"><h3><LockKeyhole />修改密码</h3>
          <div className="account-form password-form">
            <label><span>当前密码</span><input type="password" value={currentPassword} onChange={event => setCurrentPassword(event.target.value)} autoComplete="current-password"/></label>
            <label><span>新密码</span><input type="password" value={newPassword} onChange={event => setNewPassword(event.target.value)} autoComplete="new-password" placeholder="至少 6 位"/></label>
            <Button tone="paper" onClick={savePassword}>修改密码</Button>
          </div>
        </section>
        <section className="panel"><h3><Bookmark />我的收藏</h3>
          {favorites.length ? <div className="space-list">{favorites.map((item:any) => <button key={item.id} onClick={() => location.hash=`#/work/${item.slug}`}><div><b>{item.title}</b><small>{item.author_text} · {item.views || 0} 次阅览</small></div><ArrowRight/></button>)}</div> : <div className="panel-empty">收藏夹还是空的。</div>}
        </section>
        <section className="panel"><h3><BookOpen />阅读进度</h3>
          {progress.length ? <div className="space-list">{progress.map((item:any) => <button key={item.work_id} onClick={() => location.hash=`#/work/${item.slug}`}><div><b>{item.title}</b><small>已阅读 {Math.round((item.progress || 0) * 100)}% · {new Date(item.updated_at).toLocaleDateString()}</small></div><ArrowRight/></button>)}</div> : <div className="panel-empty">还没有阅读记录。</div>}
        </section>
      </div>
    </section>
  </main>
}

function AdminPage({ user }: { user: User | null }) {
  const [key, setKey] = useState('')
  const [unlocked, setUnlocked] = useState(Boolean(session.adminTicket()))
  const [message, setMessage] = useState('')
  const [actionBusy, setActionBusy] = useState('')
  const [tab, setTab] = useState('submissions')
  const [summary, setSummary] = useState<any>({})
  const [users, setUsers] = useState<any[]>([])
  const [submissions, setSubmissions] = useState<any[]>([])
  const [works, setWorks] = useState<any[]>([])
  const [editingWork, setEditingWork] = useState<any>()
  const [editWorkText, setEditWorkText] = useState('')
  const [comments, setComments] = useState<any[]>([])
  const [tracks, setTracks] = useState<any[]>([])
  const [logs, setLogs] = useState<any[]>([])
  const [feedback, setFeedback] = useState<any[]>([])
  const [musicTitle, setMusicTitle] = useState('')
  const [musicArtist, setMusicArtist] = useState('')
  const [musicFile, setMusicFile] = useState<File | null>()
  const [musicCover, setMusicCover] = useState<File | null>()
  // 馆务上传与编辑状态

  const reload = async () => {
    if (!user || !unlocked) return
    try {
      const [summaryData, submissionData, workData, commentData, musicData] = await Promise.all([
        adminApi.summary(), adminApi.submissions(), adminApi.works(), adminApi.comments(), adminApi.music()
      ]) as any[]
      setSummary(summaryData); setSubmissions(submissionData.submissions || [])
      setWorks(workData.works || []); setComments(commentData.comments || []); setTracks(musicData.tracks || [])
      const userData: any = await adminApi.users()
      setUsers(userData.users || [])
      if (user.role === 'owner') {
        const [auditData, feedbackData]: any = await Promise.all([adminApi.audit(), adminApi.feedback()])
        setLogs(auditData.logs || [])
        setFeedback(feedbackData.feedback || [])
      }
    } catch (error:any) { setMessage(error.message || '后台数据加载失败') }
  }

  useEffect(() => { if (unlocked && user) reload() }, [unlocked, user?.id])
  useEffect(() => {
    const create = () => { setEditingWork({ id:'', type:'article', title:'', author_text:user?.display_name || user?.username || '馆务', summary:'', status:'hidden', isNew:true }); setEditWorkText('') }
    const routeAlias = () => addLegacyRoute()
    window.addEventListener('zm:admin-create-work', create)
    window.addEventListener('zm:admin-route-alias', routeAlias)
    return () => { window.removeEventListener('zm:admin-create-work', create); window.removeEventListener('zm:admin-route-alias', routeAlias) }
  }, [user?.id])

  if (!user || !['owner', 'admin'].includes(user.role)) return <main>
    <PageHero eyebrow="ARCHIVE ADMINISTRATION" title="馆务区域" text="此区域仅向已授权馆员开放。" />
    <section className="section"><EmptyState title="无访问权限" text="普通账号不能进入馆务后台。" /></section>
  </main>

  const unlock = async (event: FormEvent) => {
    event.preventDefault()
    try { await adminApi.unlock(key); setUnlocked(true); setMessage('') }
    catch { setMessage('馆务密钥错误、长度不足或已经失效。') }
  }
  if (!unlocked) return <main className="gate-page">
    <form className="gate-card" onSubmit={unlock}>
      <Brand /><div className="gate-icon"><LockKeyhole /></div><small>ARCHIVE SECURITY GATE</small>
      <h1>馆务二次验证</h1>
      <p>角色校验已经通过。还需输入 Cloudflare Secret 中配置的独立馆务密钥；任何随意字符串都不能进入后台。</p>
      <input type="password" value={key} onChange={event => setKey(event.target.value)} placeholder="输入至少 12 位馆务密钥" />
      {message && <div className="form-message">{message}</div>}
      <Button type="submit"><ShieldCheck />验证并进入</Button>
      <button type="button" className="back-link" onClick={() => go('home')}><ChevronLeft />返回书阁</button>
    </form>
  </main>

  const updateUser = async (id:string, patch:any) => {
    if (actionBusy) return
    setActionBusy(`user:${id}`)
    try { await adminApi.updateUser(id, patch); await reload() }
    catch (error:any) { setMessage(error.message || '用户操作失败') }
    finally { setActionBusy('') }
  }
  const editNickname = async (item:any) => {
    const displayName = prompt('修改用户昵称', item.display_name || item.username)?.trim()
    if (!displayName) return
    await updateUser(item.id, { displayName })
  }
  const userGroups = [
    { id:'pending', title:'待审核账号', users:users.filter(item => item.status === 'pending' && item.role === 'user') },
    ...(user.role === 'owner' ? [{ id:'admins', title:'管理员', users:users.filter(item => item.status === 'active' && item.role === 'admin') }] : []),
    { id:'members', title:'普通用户', users:users.filter(item => item.status === 'active' && item.role === 'user') },
    { id:'blocked', title:'已封禁', users:users.filter(item => item.status === 'banned' && (user.role === 'owner' || item.role === 'user')) }
  ]
  const auditGroups = (() => {
    const startToday = new Date(); startToday.setHours(0,0,0,0)
    const startYesterday = new Date(startToday.getTime() - 86400000)
    const startWeek = new Date(startToday.getTime() - 6 * 86400000)
    return [
      ['今天', logs.filter(item => new Date(item.created_at) >= startToday)],
      ['昨天', logs.filter(item => { const date = new Date(item.created_at); return date >= startYesterday && date < startToday })],
      ['本周', logs.filter(item => { const date = new Date(item.created_at); return date >= startWeek && date < startYesterday })],
      ['更早', logs.filter(item => new Date(item.created_at) < startWeek)]
    ].filter(([,items]:any) => items.length)
  })()
  const review = async (id:string, status:string) => {
    if (actionBusy) return
    const note = prompt('审核说明（可留空）') || ''
    setActionBusy(`review:${id}`)
    try { await adminApi.review(id, status, note); await reload() }
    catch (error:any) { setMessage(error.message || '审核失败') }
    finally { setActionBusy('') }
  }
  const updateWork = async (id:string, patch:any) => {
    if (actionBusy) return
    setActionBusy(`work:${id}`)
    try { await adminApi.updateWork(id, patch); await reload() }
    catch (error:any) { setMessage(error.message || '文章操作失败') }
    finally { setActionBusy('') }
  }
  const openNewWorkEditor = () => {
    setEditingWork({ id:'', type:'article', title:'', author_text:user.display_name || user.username, summary:'', status:'hidden', isNew:true })
    setEditWorkText('')
  }
  const openWorkEditor = async (id:string) => {
    if (id === '__new__') return openNewWorkEditor()
    const result:any = await adminApi.workDetail(id)
    setEditingWork(result.work)
    setEditWorkText(result.work.plain_text || result.work.chapters?.map((chapter:any) => `${chapter.title}\n\n${chapter.plain_text || ''}`).join('\n\n') || '')
  }
  const saveWorkEditor = async () => {
    if (!editingWork || actionBusy) return
    if (!editingWork.title?.trim() || !editingWork.author_text?.trim() || (editingWork.type !== 'download' && !editWorkText.trim())) return setMessage('标题、作者和正文不能为空。')
    setActionBusy(editingWork.isNew ? 'work:new' : `work:${editingWork.id}`)
    try {
      if (editingWork.isNew) await adminApi.createWork({ type:editingWork.type, title:editingWork.title, authorText:editingWork.author_text, summary:editingWork.summary, status:editingWork.status, plainText:editWorkText })
      else await adminApi.updateWork(editingWork.id, { title:editingWork.title, authorText:editingWork.author_text, summary:editingWork.summary, status:editingWork.status, plainText:editWorkText })
      setEditingWork(undefined); setEditWorkText(''); setMessage(editingWork.isNew ? '馆务文章已创建。' : '文章新版本已保存。'); await reload()
    } catch (error:any) { setMessage(error.message || '文章保存失败') }
    finally { setActionBusy('') }
  }
  const deleteWork = async (id:string) => { if (confirm('确定软删除这部作品吗？')) { await adminApi.deleteWork(id); await reload() } }
  const addLegacyRoute = async () => {
    if (user.role !== 'owner') return setMessage('只有站长可以维护旧路由。')
    setMessage('请依次输入旧路由信息。')
    const oldSlug = prompt('输入旧作品 slug（不含域名和 #）')?.trim()
    const targetSlug = prompt('输入当前作品 slug（不含域名和 #）')?.trim()
    const targetType = prompt('目标类型：articles / books / collections','articles')?.trim()
    if (!oldSlug || !targetSlug || !['articles','books','collections'].includes(targetType || '')) return setMessage('旧路由参数不完整。')
    await adminApi.createRouteAlias(`/api/works/${oldSlug}`, `/${targetType}/${targetSlug}`)
    setMessage(`旧地址 ${oldSlug} 已指向 ${targetType}/${targetSlug}。`)
  }
  const deleteComment = async (id:string) => { if (confirm('确定删除这条评论吗？')) { await adminApi.deleteComment(id); await reload() } }
  const handleFeedback = async (id:string, status:string) => {
    const reply = prompt('给用户的回复（可留空）') || ''
    await adminApi.updateFeedback(id, { status, reply })
    await reload()
  }
  const editTrack = async (item:any) => {
    if (actionBusy) return
    const title = prompt('修改曲目名称', item.title)?.trim()
    if (!title) return
    const artist = prompt('修改艺术家 / 来源', item.artist || '') ?? (item.artist || '')
    const sortOrder = Number(prompt('排序数字（越小越靠前）', String(item.sort_order || 0)) || item.sort_order || 0)
    setActionBusy(`music:${item.id}`)
    try { await adminApi.updateMusic(item.id, { title, artist, sortOrder, status:item.status || 'published' }); setMessage('曲目信息已更新。'); await reload() }
    catch (error:any) { setMessage(error.message || '曲目更新失败') }
    finally { setActionBusy('') }
  }
  const toggleTrack = async (item:any) => {
    if (actionBusy) return
    setActionBusy(`music:${item.id}`)
    try { await adminApi.updateMusic(item.id, { title:item.title, artist:item.artist, sortOrder:item.sort_order, status:item.status === 'hidden' ? 'published' : 'hidden' }); await reload() }
    catch (error:any) { setMessage(error.message || '曲目状态修改失败') }
    finally { setActionBusy('') }
  }
  const removeTrack = async (item:any) => {
    if (actionBusy || !confirm(`确定删除曲目“${item.title}”吗？`)) return
    setActionBusy(`music:${item.id}`)
    try { await adminApi.deleteMusic(item.id); setMessage('曲目已删除。'); await reload() }
    catch (error:any) { setMessage(error.message || '曲目删除失败') }
    finally { setActionBusy('') }
  }
  const uploadMusic = async () => {
    if (!musicFile || !musicTitle.trim()) return setMessage('曲目名和音频文件不能为空。')
    try {
      const audio:any = await upload(musicFile, 'music')
      let coverAttachmentId
      if (musicCover) {
        const cover:any = await upload(musicCover, 'content')
        coverAttachmentId = cover.attachmentId || cover.id
      }
      await adminApi.createMusic({ attachmentId: audio.attachmentId || audio.id, coverAttachmentId, title: musicTitle, artist: musicArtist })
      setMusicTitle(''); setMusicArtist(''); setMusicFile(null); setMusicCover(null)
      setMessage('曲目已经进入公共播放列表。'); await reload()
    } catch (error:any) { setMessage(error.message || '音乐上传失败') }
  }

  const tabs = [
    ['users','用户审核'],
    ['submissions','投稿审核'],['works','馆藏管理'],['comments','评论秩序'],['music','音乐典藏'],
    ...(user.role === 'owner' ? [['feedback','用户反馈'],['audit','安全审计']] : [])
  ]

  return <main>
    <PageHero eyebrow="ARCHIVE CONSOLE · 馆务后台" title={user.role === 'owner' ? '站长总览' : '管理员工作台'}
      text={user.role === 'owner' ? '站长拥有账号治理权；所有高风险操作写入审计日志。' : '管理员只能管理普通用户内容，不能处置站长与其他管理员。'} />
    <section className={`section admin-console ${actionBusy ? 'is-busy' : ''}`} aria-busy={Boolean(actionBusy)}>
      <div className="admin-stats"><div><span>待审账号</span><strong>{summary.pendingUsers ?? '—'}</strong></div><div><span>待审投稿</span><strong>{summary.submissions ?? '—'}</strong></div><div><span>公开馆藏</span><strong>{summary.works ?? '—'}</strong></div><div><span>注册读者</span><strong>{summary.users ?? '—'}</strong></div></div>
      <div className="admin-tabs">{tabs.map(([id,label]) => <button className={tab === id ? 'active' : ''} onClick={() => setTab(id)} key={id}>{label}</button>)}<button onClick={reload}>刷新</button></div>
      {message && <div className="form-message">{message}</div>}

      {tab === 'users' && <div className="user-governance">
        {user.role === 'owner' && users.filter(item => item.role === 'owner').map(item => <article className="owner-account-card" key={item.id}><ShieldCheck/><div><small>站长账号</small><b>{item.display_name || item.username}</b><span>@{item.username}</span></div><button onClick={() => editNickname(item)}>修改昵称</button></article>)}
        {userGroups.map(group => <details className={`user-group group-${group.id}`} key={group.id} open={group.id === 'pending'}>
          <summary><span>{group.title}</span><b>{group.users.length}</b></summary>
          <div className="admin-table">{group.users.length ? group.users.map(item => <article key={item.id}>
            <div className="admin-avatar">{item.pending_avatar_url || item.avatar_url ? <img src={item.pending_avatar_url || item.avatar_url}/> : <UserRound/>}</div>
            <div><b>{item.display_name || item.username}</b><small>@{item.username} · {item.role} · {item.status}</small><time>{new Date(item.created_at).toLocaleString()}</time></div>
            <div className="row-actions">
              <button onClick={() => editNickname(item)}>修改昵称</button>
              {item.status === 'pending' && <><button className="approve" onClick={() => updateUser(item.id,{status:'active'})}>通过账号</button><button className="danger" onClick={() => updateUser(item.id,{status:'banned'})}>拒绝</button></>}
              {item.avatar_status === 'pending' && <><button onClick={() => updateUser(item.id,{avatarAction:'approve'})}>通过头像</button><button onClick={() => updateUser(item.id,{avatarAction:'reject'})}>拒绝头像</button></>}
              {user.role === 'owner' && item.status === 'active' && <button onClick={() => updateUser(item.id,{role:item.role === 'admin' ? 'user' : 'admin'})}>设为{item.role === 'admin' ? '普通用户' : '管理员'}</button>}
              {item.status !== 'pending' && <button className="danger" onClick={() => updateUser(item.id,{status:item.status === 'banned' ? 'active' : 'banned'})}>{item.status === 'banned' ? '解封' : '封禁'}</button>}
            </div>
          </article>) : <div className="panel-empty">该分区暂无账号。</div>}</div>
        </details>)}
      </div>}

      {tab === 'submissions' && <div className="admin-table">{submissions.length ? submissions.map(item => <article key={item.id}><div className="admin-type">{item.proposed_type}</div>
        <div><b>{item.title}</b><small>{item.author_text} · 投稿人 {item.display_name || item.username} · {item.status}</small><p>{item.metadata?.summary || '无简介'}</p></div>
        <div className="row-actions">{item.status === 'submitted' && <><button onClick={() => review(item.id,'approved')}>审核发布</button><button onClick={() => review(item.id,'changes_requested')}>退回修改</button><button className="danger" onClick={() => review(item.id,'rejected')}>拒绝</button></>}</div>
      </article>) : <div className="panel-empty">没有待审投稿。</div>}</div>}

      {tab === 'works' && <><div className="admin-table">{works.map(item => <article key={item.id}><div className="admin-type">{item.type}</div>
        <div><b>{item.title}</b><small>{item.author_text} · {item.owner_display_name || item.owner_username || '馆务'} · {item.status}</small></div>
        <div className="row-actions"><button onClick={() => { location.hash = `#/${item.type === 'collection' ? 'collections' : item.type === 'download' ? 'books' : 'articles'}/${item.slug}` }}>查看</button><button onClick={() => openWorkEditor(item.id)}>编辑文章</button><button onClick={() => updateWork(item.id,{status:item.status === 'published' ? 'hidden' : 'published'})}>{item.status === 'published' ? '隐藏' : '发布'}</button><button className="danger" onClick={() => deleteWork(item.id)}>删除</button></div>
      </article>)}</div>{editingWork && <div className="admin-editor-layer" onClick={() => setEditingWork(undefined)}><section className="admin-work-editor" onClick={event => event.stopPropagation()}><header><div><small>ARTICLE EDITOR</small><h2>编辑文章</h2></div><button onClick={() => setEditingWork(undefined)}><X/></button></header><div className="form-grid"><label><span>标题</span><input value={editingWork.title || ''} onChange={event => setEditingWork({...editingWork,title:event.target.value})}/></label><label><span>作者</span><input value={editingWork.author_text || ''} onChange={event => setEditingWork({...editingWork,author_text:event.target.value})}/></label><label className="full-field"><span>简介</span><textarea rows={4} value={editingWork.summary || ''} onChange={event => setEditingWork({...editingWork,summary:event.target.value})}/></label><label><span>状态</span><select value={editingWork.status || 'published'} onChange={event => setEditingWork({...editingWork,status:event.target.value})}><option value="published">公开</option><option value="hidden">隐藏</option><option value="archived">归档</option></select></label></div><label className="admin-body-editor"><span>正文纯文本</span><textarea rows={18} value={editWorkText} onChange={event => setEditWorkText(event.target.value)} placeholder="保存时会生成新的文章版本。支持脚注、注音和强调语法。"/></label><footer><Button tone="paper" onClick={() => setEditingWork(undefined)}>取消</Button><Button onClick={saveWorkEditor}>保存新版本</Button></footer></section></div>}</>}

      {tab === 'comments' && <div className="admin-table">{comments.map(item => <article key={item.id}><div className="admin-type"><MessageCircle/></div>
        <div><b>{item.display_name || item.username}</b><small>{item.target_type === 'forum' ? '论坛回复' : '作品评论'} · {item.role} · {new Date(item.created_at).toLocaleString()}</small><p>{item.body?.text || '图片评论'}</p></div>
        <div className="row-actions"><button className="danger" onClick={() => deleteComment(item.id)}>删除评论</button></div>
      </article>)}</div>}
      {tab === 'music' && <div className="music-admin">
        <div className="music-upload-form"><h3>上传公共曲目</h3><input value={musicTitle} onChange={event => setMusicTitle(event.target.value)} placeholder="曲目名称 *"/><input value={musicArtist} onChange={event => setMusicArtist(event.target.value)} placeholder="艺术家 / 来源"/>
          <label className="file-control"><Music2/><div><b>{musicFile?.name || '选择音频文件'}</b><small>MP3 / M4A / OGG / WAV / FLAC</small></div><input type="file" accept="audio/*" onChange={event => setMusicFile(event.target.files?.[0] || null)}/></label>
          <label className="file-control"><Upload/><div><b>{musicCover?.name || '选择曲目封面（可选）'}</b></div><input type="file" accept="image/*" onChange={event => setMusicCover(event.target.files?.[0] || null)}/></label><Button onClick={uploadMusic}><Upload/>上传并发布</Button>
        </div><div className="track-admin-list">{tracks.map(item => <div key={item.id}><Disc3/><span><b>{item.title}</b><small>{item.artist || '未注明艺术家'} · {item.status === 'hidden' ? '已隐藏' : '公开'}</small></span><div className="track-actions"><button onClick={() => editTrack(item)}>重命名 / 编辑</button><button onClick={() => toggleTrack(item)}>{item.status === 'hidden' ? '恢复' : '隐藏'}</button><button className="danger" onClick={() => removeTrack(item)}>删除</button></div></div>)}</div>
      </div>}

      {tab === 'feedback' && user.role === 'owner' && <div className="admin-feedback-list">
        {feedback.length ? feedback.map(item => <article key={item.id}>
          <div className={`feedback-status status-${item.status}`}>
            {item.status === 'open' ? '待查看' : item.status === 'processing' ? '处理中' : item.status === 'resolved' ? '已解决' : '已驳回'}
          </div>
          <div className="admin-feedback-copy">
            <small>{item.display_name || item.username} · {item.category} · {new Date(item.created_at).toLocaleString()}</small>
            <h3>{item.title}</h3><p>{item.body}</p>
            {item.page_url && <a href={item.page_url} target="_blank" rel="noreferrer">查看反馈页面</a>}
            {item.image_url && <img src={item.image_url} alt="反馈截图"/>}
            {item.owner_reply && <blockquote><b>当前回复</b>{item.owner_reply}</blockquote>}
          </div>
          <div className="row-actions">
            <button onClick={() => handleFeedback(item.id,'processing')}>标记处理中</button>
            <button onClick={() => handleFeedback(item.id,'resolved')}>回复并解决</button>
            <button className="danger" onClick={() => handleFeedback(item.id,'rejected')}>驳回</button>
          </div>
        </article>) : <div className="panel-empty">暂时没有用户反馈。</div>}
      </div>}

      {tab === 'audit' && user.role === 'owner' && <div className="audit-timeline">{auditGroups.length ? auditGroups.map(([label,items]:any) => <details key={label} open={label === '今天'}>
        <summary><span>{label}</span><b>{items.length} 条操作</b></summary>
        <div className="admin-table audit-list">{items.slice(0,12).map((item:any) => <article key={item.id}><div className="admin-type"><ShieldCheck/></div><div><b>{item.action}</b><small>{item.actor_username || '系统'} · {item.target_type || 'system'}</small><time>{new Date(item.created_at).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'})}</time></div></article>)}</div>
        {items.length > 12 && <div className="audit-more">该时间段另有 {items.length - 12} 条，完整记录仍保存在 D1。</div>}
      </details>) : <div className="panel-empty">暂无审计记录。</div>}</div>}
    </section>
  </main>
}

function AuthModal({ open, close, completed }: { open: boolean; close: () => void; completed: (user: User) => void }) {
  const [mode, setMode] = useState<'login' | 'register'>('login')
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState('')

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (username.trim().length < 3 || password.length < 6) return setMessage('用户名至少 3 位，密码至少 6 位。')
    if (mode === 'register' && displayName.trim().length < 2) return setMessage('请填写至少 2 个字符的昵称。')
    setBusy(true)
    try {
      let result: any
      if (mode === 'register') {
        await authApi.register(username.trim(), password, displayName.trim())
        setMessage('账号已提交审核。站长通过后即可登录，并在个人书房上传头像。')
        setMode('login')
        setPassword('')
        return
      } else {
        result = await authApi.login(username.trim(), password)
        completed(result.user || result)
      }
      close()
    } catch (error: any) {
      const code = String(error.message || '')
      const translated: Record<string,string> = {
        ACCOUNT_PENDING_REVIEW:'账号正在等待站长审核，通过后即可登录。',
        ACCOUNT_BANNED:'账号已被封禁，请联系站长。',
        INVALID_CREDENTIALS:'用户名或密码不正确。',
        NICKNAME_REQUIRED:'创建账号必须填写昵称。',
        SECURE_CONTEXT_REQUIRED:'登录注册必须通过 HTTPS 安全连接进行。',
        CREDENTIAL_KEY_EXPIRED:'加密会话已过期，请重新提交。',
        ENCRYPTED_CREDENTIAL_REQUIRED:'客户端未发送加密密码，请刷新页面重试。'
      }
      setMessage(code === 'Failed to fetch' ? '无法连接 Worker，请检查 API 地址与 CORS 设置。' : (translated[code] || code || '操作失败。'))
    } finally { setBusy(false) }
  }

  return <AnimatePresence>{open && <motion.div className="modal-layer" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
    onMouseDown={event => event.target === event.currentTarget && close()}>
    <motion.div className="auth-card" initial={{ opacity: 0, scale: .94, y: 25 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: .96 }}>
      <button className="modal-close" onClick={close}><X /></button><Brand />
      <small>MEMBER ACCESS</small><h2>{mode === 'login' ? '回到你的书房' : '成为书阁的一员'}</h2>
      <p>{mode === 'login' ? '继续上一次阅读，并参与严肃讨论。' : '创建账号后，头像需要馆务审核才会公开。'}</p>
      <div className="auth-tabs"><button className={mode === 'login' ? 'active' : ''} onClick={() => setMode('login')}>登录</button>
        <button className={mode === 'register' ? 'active' : ''} onClick={() => setMode('register')}>创建账号</button></div>
      <form onSubmit={submit}>
        {mode === 'register' && <label><span>昵称</span><input value={displayName} onChange={event => setDisplayName(event.target.value)} placeholder="评论区和作品互动中显示的名字" maxLength={30} /></label>}
        <label><span>用户名</span><input autoComplete="username" value={username} onChange={event => setUsername(event.target.value)} placeholder="3—24 位字符" /></label>
        <label><span>密码</span><input type="password" autoComplete={mode === 'login' ? 'current-password' : 'new-password'} value={password} onChange={event => setPassword(event.target.value)} placeholder="至少 6 位" /></label>
        {message && <div className="form-message">{message}</div>}
        <Button type="submit" disabled={busy}>{busy ? <><span className="loading-orbit" />{mode === 'login' ? '正在登录' : '正在提交'}</> : <>{mode === 'login' ? '登录书阁' : '提交审核'}<ArrowRight /></>}</Button>
      </form>
      <div className="security-note"><LockKeyhole />密码通过临时 ECDH + AES-GCM 加密传输，并只以加盐哈希保存；请始终使用 HTTPS 访问。</div>
    </motion.div>
  </motion.div>}</AnimatePresence>
}

let sharedAudio: HTMLAudioElement | undefined
const getSharedAudio = () => sharedAudio || (sharedAudio = new Audio())

function MusicPlayer() {
  const [hidden, setHidden] = useState(() => localStorage.getItem('zm_player_hidden') === '1')
  const [tracks, setTracks] = useState<any[]>([])
  const [index, setIndex] = useState(() => Number(localStorage.getItem('zm_track_index') || 0))
  const [playing, setPlaying] = useState(false)
  const [muted, setMuted] = useState(() => localStorage.getItem('zm_player_muted') === '1')
  const [random, setRandom] = useState(() => localStorage.getItem('zm_player_random') === '1')
  const audio = useMemo(getSharedAudio, [])

  useEffect(() => {
    audio.preload = 'metadata'
    musicApi.list().then((result:any) => setTracks(result.tracks || [])).catch(() => setTracks([]))
    const savePosition = () => { if (audio.currentTime > 0) localStorage.setItem('zm_track_position', String(audio.currentTime)) }
    window.addEventListener('pagehide', savePosition)
    return () => window.removeEventListener('pagehide', savePosition)
  }, [audio])

  const track = tracks[index % Math.max(1, tracks.length)]
  useEffect(() => {
    if (!track) return
    const changed = audio.src !== new URL(track.audio_url, location.href).href
    if (changed) audio.src = track.audio_url
    audio.volume = Number(localStorage.getItem('zm_volume') || .72)
    audio.muted = muted
    if (changed) {
      const resumeAt = Number(localStorage.getItem('zm_track_position') || 0)
      audio.addEventListener('loadedmetadata', () => { if (resumeAt > 0 && resumeAt < audio.duration) audio.currentTime = resumeAt }, { once: true })
    }
    if (playing) audio.play().catch(() => setPlaying(false))
    localStorage.setItem('zm_track_index', String(index))
  }, [track?.id])

  useEffect(() => {
    const ended = () => next()
    audio.addEventListener('ended', ended)
    return () => audio.removeEventListener('ended', ended)
  })

  const toggle = async () => {
    if (!track) return
    if (playing) audio.pause()
    else await audio.play().catch(() => undefined)
    setPlaying(!playing)
  }
  const next = () => {
    if (!tracks.length) return
    let nextIndex = random ? Math.floor(Math.random() * tracks.length) : (index + 1) % tracks.length
    if (random && tracks.length > 1 && nextIndex === index) nextIndex = (nextIndex + 1) % tracks.length
    localStorage.removeItem('zm_track_position')
    setIndex(nextIndex)
  }
  const previous = () => {
    if (!tracks.length) return
    localStorage.removeItem('zm_track_position')
    setIndex((index - 1 + tracks.length) % tracks.length)
  }
  const hide = () => { if (audio.currentTime > 0) localStorage.setItem('zm_track_position',String(audio.currentTime)); audio.pause(); setPlaying(false); setHidden(true); localStorage.setItem('zm_player_hidden','1') }
  const recall = () => { setHidden(false); localStorage.removeItem('zm_player_hidden') }

  if (hidden) return <button className="player-recall" onClick={recall}><Music2 /><span>召回音乐</span></button>
  return <div className="music-player">
    <div className={`record ${playing ? 'playing' : ''}`}>{track?.cover_url ? <img src={track.cover_url}/> : <Disc3 />}</div>
    <div className="track-copy"><small>公共播放列表 · {tracks.length} 首</small><b>{track ? `${track.title}${track.artist ? ` · ${track.artist}` : ''}` : '等待馆务上传第一首曲目'}</b></div>
    <div className="player-buttons">
      <button className={random ? 'active' : ''} onClick={() => { const value = !random; setRandom(value); localStorage.setItem('zm_player_random', value ? '1' : '0') }}><Shuffle /></button><button onClick={previous}><SkipBack /></button>
      <button className="play-button" onClick={toggle}>{playing ? <Pause /> : <Play />}</button>
      <button onClick={next}><SkipForward /></button><button className={muted ? 'active' : ''} onClick={() => { const value=!muted;setMuted(value);audio.muted=value;localStorage.setItem('zm_player_muted',value?'1':'0') }}><Volume2 /></button>
    </div>
    <button className="player-close" onClick={hide}><X /></button>
  </div>
}

function FloatingNavigator() {
  const [open, setOpen] = useState(false)
  const items: Array<[View,string,ReactNode]> = [
    ['home','首页',<BookOpen/>],['library','书库',<Library/>],['tags','标签',<Sparkles/>],['submit','投稿',<PenLine/>],['feedback','反馈',<MessageCircle/>]
  ]
  return <aside className={`floating-nav ${open ? 'open' : ''}`}>
    <button className="floating-trigger" onClick={() => setOpen(!open)} aria-label="展开快捷导航"><Sparkles/></button>
    <div>{items.map(([view,label,icon]) => <button key={view} onClick={() => { go(view); setOpen(false) }}>{icon}<span>{label}</span></button>)}
      <button onClick={() => window.scrollTo({top:0,behavior:'smooth'})}><ChevronLeft/><span>顶部</span></button>
    </div>
  </aside>
}

function ScrollRevealController() {
  useEffect(() => {
    const nodes = Array.from(document.querySelectorAll<HTMLElement>('.section,.rights-notice'))
    nodes.forEach(node => node.classList.add('reveal-section'))
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => { if (entry.isIntersecting) { entry.target.classList.add('revealed'); observer.unobserve(entry.target) } })
    }, { rootMargin: '0px 0px -8% 0px', threshold: .08 })
    nodes.forEach(node => observer.observe(node))
    return () => observer.disconnect()
  })
  return null
}

const CHANGELOG_VERSION = '6.0.0-rc1'
function ChangelogModal() {
  const [open, setOpen] = useState(() => localStorage.getItem('zm_changelog_seen') !== CHANGELOG_VERSION)
  useEffect(() => {
    const show = () => setOpen(true)
    window.addEventListener('zm:show-changelog', show)
    return () => window.removeEventListener('zm:show-changelog', show)
  }, [])
  const close = () => { localStorage.setItem('zm_changelog_seen', CHANGELOG_VERSION); setOpen(false) }
  return <AnimatePresence>{open && <motion.div className="modal-layer changelog-layer" initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}} onMouseDown={event => event.target === event.currentTarget && close()}>
    <motion.section className="changelog-card" initial={{opacity:0,y:24,scale:.96}} animate={{opacity:1,y:0,scale:1}} exit={{opacity:0,y:16}}>
      <button className="modal-close" onClick={close}><X/></button><span className="section-kicker">WHAT'S NEW · V6</span><h2>争鸣书阁正在升级</h2><p>本次更新聚焦阅读体验、馆务能力与安全性。</p>
      <ul><li>三套主题统一阅读器、菜单、弹窗和后台配色。</li><li>新增千字分页/连续滚动阅读、目录抽屉、脚注与注音。</li><li>标签云移至独立标签页，无封面作品生成 SVG 书封。</li><li>论坛支持多层回复，管理员可审核用户与编辑文章。</li><li>音乐支持重命名、排序、隐藏和删除。</li><li>登录注册密码增加临时 ECDH + AES-GCM 应用层加密。</li></ul>
      <div className="changelog-actions"><Button onClick={close}>知道了，开始阅读</Button></div>
    </motion.section>
  </motion.div>}</AnimatePresence>
}

function Footer() {
  return <footer className="site-footer">
    <Brand /><p>为等待历史天使群服务的人文社科公共图书馆。愿每一次争鸣，都使理解更接近真实。</p>
    <div><button onClick={() => go('library')}>书库</button><button onClick={() => go('tags')}>标签</button><button onClick={() => go('submit')}>投稿</button><button onClick={() => go('feedback')}>反馈</button><button onClick={() => go('profile')}>个人书房</button><button onClick={() => window.dispatchEvent(new Event('zm:show-changelog'))}>更新日志</button></div>
    <small>© {new Date().getFullYear()} 争鸣书阁 · Cloudflare Pages / Workers / D1 / R2 / KV</small>
  </footer>
}

export default function App() {
  const [view, setView] = useState<View>(currentView())
  const [routeKey, setRouteKey] = useState(() => location.hash || '#/home')
  const [user, setUser] = useState<User | null>(() => null)
  const [authOpen, setAuthOpen] = useState(false)
  const [works, setWorks] = useState<Work[]>([])
  const [loading, setLoading] = useState(true)
  const [theme, setThemeState] = useState<ThemeName>(() => {
    const saved = localStorage.getItem('zm_theme')
    return saved === 'sakura' || saved === 'nebula' || saved === 'obsidian' ? saved : 'sakura'
  })

  const setTheme = (next: ThemeName) => {
    setThemeState(next)
    localStorage.setItem('zm_theme', next)
    document.documentElement.dataset.theme = next
  }

  useEffect(() => {
    document.documentElement.dataset.theme = theme
  }, [theme])

  useEffect(() => {
    if (location.hash || location.pathname === '/') return
    const path = location.pathname.replace(/^\/+|\/+$/g,'')
    if (/^(articles|books|collections)\/[^/]+$/.test(path)) location.replace(`${location.origin}${location.pathname.replace(path,'')}#/${path}`)
    else if (routeAliases[path]) location.replace(`${location.origin}${location.pathname.replace(path,'')}#/${routeAliases[path]}`)
  }, [])

  useEffect(() => {
    const releasePointerFocus = (event: PointerEvent) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return
      const target = event.target as HTMLElement | null
      const control = target?.closest<HTMLElement>('button,[role="button"],summary,a')
      if (control && control.matches(':focus') && !control.matches(':focus-visible')) control.blur()
    }
    document.addEventListener('pointerup', releasePointerFocus)
    return () => document.removeEventListener('pointerup', releasePointerFocus)
  }, [])

  useEffect(() => {
    let disposed = false
    const checkBuild = async () => {
      try {
        const response = await fetch(`/build-version.json?t=${Date.now()}`, {
          cache: 'no-store',
          headers: { 'Cache-Control': 'no-cache' }
        })
        if (!response.ok) return
        const info = await response.json() as { buildId?: string }
        if (!info.buildId || disposed) return

        const current = localStorage.getItem('zm_build_id')
        if (!current) {
          localStorage.setItem('zm_build_id', info.buildId)
          return
        }
        if (current !== info.buildId) {
          localStorage.setItem('zm_build_id', info.buildId)
          if ('caches' in window) {
            const keys = await caches.keys()
            await Promise.all(keys.map(key => caches.delete(key)))
          }
          const url = new URL(location.href)
          url.searchParams.set('build', info.buildId)
          location.replace(url.toString())
        }
      } catch {
        // 离线或网络波动时保持当前页面，不阻断阅读。
      }
    }

    checkBuild()
    const timer = window.setInterval(checkBuild, 5 * 60 * 1000)
    return () => {
      disposed = true
      window.clearInterval(timer)
    }
  }, [])

  useEffect(() => {
    const update = () => {
      setView(currentView())
      setRouteKey(location.hash)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
    addEventListener('hashchange', update)
    if (!location.hash) go('home')
    return () => removeEventListener('hashchange', update)
  }, [])

  useEffect(() => {
    contentApi.list().then((result: any) => setWorks(result.works || result.items || result || []))
      .catch(() => setWorks([])).finally(() => setLoading(false))
    if (session.token()) authApi.me().then((result: any) => setUser(result.user || result)).catch(() => session.clear())
  }, [])

  const logout = async () => {
    await authApi.logout()
    setUser(null)
    go('home')
  }

  return <div className={`app theme-${theme}`}>
    <div className="grain" aria-hidden="true" />
    <ScrollRevealController />
    <Header
      user={user}
      openAuth={() => setAuthOpen(true)}
      theme={theme}
      setTheme={setTheme}
    />
    <AnimatePresence mode="wait">
      <motion.div key={routeKey} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
        transition={{ duration: .32, ease: [.22, 1, .36, 1] }}>
        {view === 'home' && <Home works={works} />}
        {view === 'library' && <LibraryPage works={works} loading={loading} />}
        {view === 'tags' && <TagsPage />}
        {view === 'forum' && <Suspense fallback={<div className="loading"><span className="loading-orbit"/>正在进入论坛</div>}><ForumPage user={user} openAuth={() => setAuthOpen(true)}/></Suspense>}
        {['work','articles','books','collections'].includes(view) && <WorkPage user={user} openAuth={() => setAuthOpen(true)} />}
        {view === 'submit' && <SubmitPage user={user} openAuth={() => setAuthOpen(true)} />}
        {view === 'feedback' && <FeedbackPage user={user} openAuth={() => setAuthOpen(true)} />}
        {view === 'profile' && <ProfilePage user={user} openAuth={() => setAuthOpen(true)} logout={logout} />}
        {view === 'admin' && <AdminPage user={user} />}
      </motion.div>
    </AnimatePresence>
    <Footer />
    <FloatingNavigator />
    {view === 'admin' && (user?.role === 'owner' || user?.role === 'admin') && <div className="admin-fab-group">{user.role === 'owner' && <button className="admin-route-fab" onClick={() => window.dispatchEvent(new Event('zm:admin-route-alias'))}>旧路由</button>}<button className="admin-create-fab" onClick={() => window.dispatchEvent(new Event('zm:admin-create-work'))}><Plus/><span>新建文章</span></button></div>}
    <MusicPlayer />
    <AuthModal open={authOpen} close={() => setAuthOpen(false)} completed={setUser} />
    <ChangelogModal />
  </div>
}
